import { comparer, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";
import type { ColumnFiltersState, RowSelectionState, SortingState } from "@tanstack/react-table";
import type { RootStore } from "./root.store";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import {
  isColumnFiltersState,
  isSortingStateOver,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import { type BuilderError, parseSaveErrors } from "@/lib/intake/parse-save-errors";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import { normalizeRichText } from "@/lib/rich-text";
import type { RequestViewFilterState } from "@/helpers/request-view.helper";
import type {
  TIntakeChoiceOption,
  TIntakeColumnsElement,
  TIntakeDefinition,
  TIntakeElement,
  TIntakeExpression,
  TIntakeFieldGroupElement,
  TIntakeForm,
  TIntakeLocale,
  TIntakeLocalizedText,
  TIntakePage,
  SubmissionGroupBy,
  SubmissionsDisplay,
  TIntakeSubmission,
  TIntakeSubmissionsArchivedView,
  TIntakeSubmissionsListQuery,
  TIntakeSystemFieldElement,
  TIntakeSystemFieldKind,
  TIntakeTreeElement,
  TWorkflowCurrentStage,
  TWorkflowDecisionResult,
  TWorkflowReturnResult,
  TStatusReasonSelection,
  TSingletonElementKind,
} from "@/types/intake";
import {
  DEFAULT_SYSTEM_FIELD_KINDS,
  OPTIONAL_SYSTEM_FIELD_KINDS,
  isRemovableSystemField,
} from "@/types/intake";
import type { TWorkspaceCustomField } from "@/types/custom-field";
import type { TRequestImportRequest, TRequestImportResult } from "@/types/intake-import";
import type { TPriorityTier } from "@/types/request-prioritization";
import {
  collectSubtreeIds,
  type ContainerPath,
  findElementById,
  getChildrenAtPath,
  iterTree,
  pathEquals,
  pathTouches,
  subtreeContainsSingleton,
} from "@/lib/intake/tree";
import { validateDraft } from "@/lib/intake/validate-draft";
import { peekQueryClient } from "@/queries/client";
import { myWorkChanged } from "@/queries/my-work";
import {
  markCachedFormsStale,
  putCachedBuilderForm,
  removeCachedBuilderForm,
} from "@/queries/request-forms";
import {
  dropCachedPendingReview,
  findCachedRequest,
  invalidateCachedStage,
  markCachedActivityStale,
  markCachedRequestListsStale,
  putCachedRequest,
  putCachedStage,
  removeCachedRequest,
  requestDetailKey,
  writeRequestRow,
  type RequestsListParams,
} from "@/queries/requests";
import { scopeIfReady } from "@/queries/scope";

/** A stored form as the builder's autosave body would send it, for diffing a
 *  save against it: the same defaults and draft overlay `hydrateFromForm` uses,
 *  and the same `description_html` normalization the save applies. */
function savedFormBody(form: TIntakeForm) {
  return {
    definition: injectMissingDefaultSystemFields(clone(form.definition)),
    name: form.name,
    description_html: normalizeRichText(form.description_html),
    teams: form.teams ?? [],
    request_type: form.request_type ?? "",
    default_request_category_value_id: form.default_request_category_value_id ?? null,
    default_request_owner_ids: form.default_request_owner_ids ?? [],
    managing_team_id: form.managing_team_id ?? form.managing_team?.id ?? null,
  };
}

function clone<T>(value: T): T {
  // Definitions are plain JSON, but values arriving from MobX observers can
  // be Proxy-wrapped, which `structuredClone` rejects. The JSON round-trip is
  // intentional: it strips any wrapper before the deep copy.
  return JSON.parse(JSON.stringify(value)) as T;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function makeNewPage(): TIntakePage {
  return {
    id: newId(),
    name: { en: "New page", fr: "New page" },
    internal: false,
    visible_if: null,
    elements: [],
  };
}

type NewElementKind = Exclude<TIntakeElement["kind"], "system_field">;

/** Palette kinds that produce layout-only elements (not value-bearing). */
export type LayoutPaletteKind = "info_text" | "rich_text" | "divider" | "field_group" | "columns";

/**
 * Maps workspace bank field kinds to their intake element kinds.
 *
 * `satisfies` rather than a `Record<…, NewElementKind>` annotation on purpose:
 * the annotation widened every value to the full element-kind union, which hid
 * the fact that `makeNewElementFromBankField` handled only some of them and
 * downgraded the rest to `text` (NG-444). Inferring literals keeps the produced
 * union exact, so that switch's exhaustiveness check is worth something — while
 * `satisfies` still proves every bank kind is mapped to a real element kind.
 */
export const BANK_TO_INTAKE_KIND = {
  text: "text",
  paragraph: "textarea",
  number: "number",
  boolean: "checkbox",
  date: "datetime",
  select: "select",
  multiselect: "checkboxes",
  email: "email",
  phone: "phone",
  currency: "currency",
  user_list: "user_list",
} as const satisfies Record<TWorkspaceCustomField["kind"], NewElementKind>;

/**
 * A fresh choice option. The stored ``value`` is a generated, opaque, stable id
 * (same approach as element ``name``): it satisfies the backend's non-empty +
 * unique-per-element contract, never changes when the label is edited (so
 * existing submissions and ``visible_if`` conditions keep resolving), and is
 * mapped back to the label through the survey ``choices`` wherever an answer is
 * rendered. The label is seeded with a human default ("Option 1", "Option 2", …)
 * keyed to the option's position, editable from the Data tab (#2950) — before
 * this a blank label fell back to the opaque id, so a freshly added choice field
 * showed a UUID as its first option.
 */
function makeOption(index = 0): TIntakeChoiceOption {
  const label = `Option ${index + 1}`;
  return { value: newId(), label: { en: label, fr: label } };
}

function makeNewElement(kind: NewElementKind): TIntakeElement {
  const baseNamed = {
    id: newId(),
    name: newId(),
    label: { en: "New field", fr: "New field" } as TIntakeLocalizedText,
    internal: false,
    required: false,
    visible_if: null,
  };
  switch (kind) {
    case "text":
    case "textarea":
    case "email":
    case "url":
    case "phone":
    case "number":
    case "currency":
    case "datetime":
    case "file":
    case "checkbox":
    // Bank-driven member multiselect; options resolve at render time, so a
    // bare named element is all a (non-palette) new instance needs.
    case "user_list":
      return { ...baseNamed, kind };
    case "radio":
    case "checkboxes":
    case "select":
      return { ...baseNamed, kind, options: [makeOption()] };
    case "program":
      // Fixed display name — the program element is a singleton system-style field,
      // seeded (not "New field") so it reads correctly the moment it's added.
      return {
        ...baseNamed,
        kind: "program",
        label: { en: "Programs", fr: "Programmes" },
      };
    case "goal":
      // Seeded like program. ``multiple`` is set explicitly rather than left
      // undefined so the default is a stated choice (multi, matching program)
      // that the properties toggle then reflects, not an accident of falsiness.
      return {
        ...baseNamed,
        kind: "goal",
        label: { en: "Goals", fr: "Objectifs" },
        multiple: true,
      };
    case "request_category":
      // Fixed display name — the request-category element is a singleton
      // system-style field, seeded (not "New field") like ``program``.
      return {
        ...baseNamed,
        kind: "request_category",
        label: { en: "Request Category", fr: "Catégorie de demande" },
      };
    case "expression":
      return {
        ...baseNamed,
        kind: "expression",
        label: { en: "Calculated value", fr: "Calculated value" },
        // Calculated values are staff-facing by default (consistent with the
        // request-owner system field); the author flips Internal off to share
        // the result with requesters. The separate per-expression ``visible``
        // toggle was consolidated away 2026-06-10 — the flag remains honored
        // by the renderer for forms that already set it.
        internal: true,
        // Aggregate (sum/min/max/avg) is what authors reach for — product
        // decision 2026-06-10 — so new calculation fields start there instead
        // of a bare literal.
        expression: { type: "aggregate", op: "sum", args: [{ type: "literal", value: 0 }] },
      };
  }
}

function makeNewElementFromBankField(field: TWorkspaceCustomField): TIntakeElement {
  const intakeKind = BANK_TO_INTAKE_KIND[field.kind];
  const baseNamed = {
    id: newId(),
    name: newId(),
    label: { en: field.name, fr: field.name } as TIntakeLocalizedText,
    internal: false,
    required: false,
    visible_if: null,
    bank_field_id: field.id,
  };
  switch (intakeKind) {
    // Every scalar kind a bank field can map to needs a bare named element —
    // list them ALL. Omitting one used to fall through to the `default:` arm
    // below and silently produce a single-line `text` element, so a Global
    // paragraph/boolean/email/phone/currency field rendered as a text input
    // while its palette tile still showed the right icon (NG-444).
    case "text":
    case "textarea":
    case "number":
    case "checkbox":
    case "email":
    case "phone":
    case "datetime":
      return { ...baseNamed, kind: intakeKind };
    case "currency":
      // Snapshot the bank field's currency code the same way the choice kinds
      // below snapshot `display`, so the amount renders in the right currency.
      return {
        ...baseNamed,
        kind: "currency" as const,
        ...(field.config.currency ? { currency: field.config.currency } : {}),
      };
    case "select":
    case "checkboxes": {
      const options = (field.config.options ?? []).map((opt) => ({
        value: opt.value,
        label: { en: opt.label, fr: opt.label },
      }));
      if (intakeKind === "checkboxes") {
        // Carry the multi-select's display preference so the form renders a
        // dropdown (tagbox) vs checkboxes — snapshotted at add-time like options.
        return {
          ...baseNamed,
          kind: "checkboxes" as const,
          options,
          ...(field.config.display === "dropdown" ? { display: "dropdown" as const } : {}),
        };
      }
      // Carry the single-select's display preference so the form renders a
      // radio group vs dropdown — snapshotted at add-time like options. The
      // live bank display still wins at render time (PRD §3.1); this is the
      // fallback for a detached field.
      return {
        ...baseNamed,
        kind: "select" as const,
        options,
        ...(field.config.display === "radio" ? { display: "radio" as const } : {}),
      };
    }
    case "user_list":
      // Options are live workspace members — resolved at render time, never stored.
      return { ...baseNamed, kind: intakeKind };
    default: {
      // Exhaustiveness guard. `BANK_TO_INTAKE_KIND` covering every BANK kind is
      // not enough — this switch has to cover every kind that map can PRODUCE,
      // and it silently didn't (NG-444). Typing the fallthrough as `never` makes
      // `tsc` fail here instead, so adding a bank kind can't quietly downgrade
      // a field to text again. Keep the runtime fallback for a malformed field
      // arriving from the API, which the compiler can't see.
      const unhandled: never = intakeKind;
      console.warn("Unhandled bank field kind, falling back to text", {
        bankFieldId: field.id,
        bankFieldKind: field.kind,
        intakeKind: unhandled,
      });
      return { ...baseNamed, kind: "text" as const };
    }
  }
}

function isSystemFieldElement(el: TIntakeTreeElement): boolean {
  return el.kind === "system_field";
}

// Locked system fields are the auto-injected page-1 defaults — they cannot be
// deleted/renamed/reordered off the form. Everything in
// REMOVABLE_SYSTEM_FIELD_KINDS is the opposite: an author can take it off.
// request_owner has been removable since #2948 / #3089 (deleting it lets the
// form-level default owner fallback take over) and stayed removable when its ADD
// affordance was dropped; NG-690's launch date is removable and addable.
//
// Read through the shared set rather than naming a code here: this guard is what
// `removeElementAtPath` consults, so a removable field missing from the set
// cannot be deleted AT ALL, and the untick in the picker fails silently.
function isLockedSystemField(el: TIntakeTreeElement): boolean {
  return el.kind === "system_field" && !isRemovableSystemField(el);
}

function makeLayoutElement(kind: LayoutPaletteKind): TIntakeTreeElement {
  switch (kind) {
    case "info_text":
      return {
        id: newId(),
        kind: "info_text",
        content: { en: "Informational text", fr: "Texte informatif" },
      };
    case "rich_text":
      return {
        id: newId(),
        kind: "rich_text",
        content: { en: "<p></p>", fr: "<p></p>" },
      };
    case "divider":
      return { id: newId(), kind: "divider" };
    case "field_group": {
      const group: TIntakeFieldGroupElement = {
        id: newId(),
        kind: "field_group",
        label: { en: "Group", fr: "Groupe" },
        collapsed: false,
        children: [],
      };
      return group;
    }
    case "columns": {
      const cols: TIntakeColumnsElement = {
        id: newId(),
        kind: "columns",
        columns: [
          { id: newId(), children: [] },
          { id: newId(), children: [] },
        ],
      };
      return cols;
    }
  }
}

export function isLayoutPaletteKind(kind: string): kind is LayoutPaletteKind {
  return (
    kind === "info_text" ||
    kind === "rich_text" ||
    kind === "divider" ||
    kind === "field_group" ||
    kind === "columns"
  );
}

/**
 * Re-assigns ``id`` (and field ``name`` for leaves) recursively on a duplicated
 * subtree. The root keeps ``rootId`` so callers can re-select the duplicate.
 */
function regenerateSubtreeIds(element: TIntakeTreeElement, rootId: string): void {
  element.id = rootId;
  if (element.kind === "field_group") {
    for (const child of element.children) regenerateSubtreeIds(child, newId());
  } else if (element.kind === "columns") {
    for (const column of element.columns) {
      column.id = newId();
      for (const child of column.children) regenerateSubtreeIds(child, newId());
    }
  } else if (element.kind !== "system_field" && "name" in element) {
    element.name = newId();
  }
}

function annotateDuplicateLabels(element: TIntakeTreeElement): void {
  if (element.kind === "system_field") return;
  if ("label" in element && element.label) {
    element.label = {
      // A locale the label lacks reads as "", never the string "undefined" (NG-3487).
      en: `${element.label.en ?? ""} (copy)`,
      fr: `${element.label.fr ?? ""} (copy)`,
    };
  }
}

/** Like {@link regenerateSubtreeIds}, but records each leaf's old→new ``name`` in
 *  *nameMap* so a caller can rewrite references that point WITHIN the subtree to
 *  the clone's new names (NG-3856). */
function regenerateSubtreeIdsTrackingNames(
  element: TIntakeTreeElement,
  rootId: string,
  nameMap: Map<string, string>,
): void {
  element.id = rootId;
  if (element.kind === "field_group") {
    for (const child of element.children)
      regenerateSubtreeIdsTrackingNames(child, newId(), nameMap);
  } else if (element.kind === "columns") {
    for (const column of element.columns) {
      column.id = newId();
      for (const child of column.children)
        regenerateSubtreeIdsTrackingNames(child, newId(), nameMap);
    }
  } else if (element.kind !== "system_field" && "name" in element) {
    const oldName = element.name;
    element.name = newId();
    nameMap.set(oldName, element.name);
  }
}

function remapExpressionRefs(expression: TIntakeExpression, nameMap: Map<string, string>): void {
  if (expression.type === "ref") {
    const mapped = nameMap.get(expression.field);
    if (mapped) expression.field = mapped;
  } else if (expression.type === "binary") {
    remapExpressionRefs(expression.left, nameMap);
    remapExpressionRefs(expression.right, nameMap);
  } else if (expression.type === "aggregate") {
    for (const arg of expression.args) remapExpressionRefs(arg, nameMap);
  }
}

function reconcileDuplicateConditionRefs(
  element: TIntakeTreeElement,
  nameMap: Map<string, string>,
): void {
  const condition = element.visible_if;
  if (!condition) return;
  const isAll = "all" in condition;
  const clauses = isAll ? condition.all : condition.any;
  // Keep a clause that points WITHIN the subtree (remapped to the clone's new name);
  // DROP one that points outside it — on the copy's new page that controller may no
  // longer precede the field, which would make the workflow unpublishable (NG-3856).
  const kept = clauses
    .filter((clause) => nameMap.has(clause.field))
    .map((clause) => ({ ...clause, field: nameMap.get(clause.field)! }));
  element.visible_if = kept.length === 0 ? null : isAll ? { all: kept } : { any: kept };
}

/**
 * Reconcile a freshly-cloned, freshly-renamed subtree's references for a DUPLICATE
 * (NG-3856): a ``visible_if`` / formula reference that points WITHIN the subtree is
 * rewritten to the clone's new name, so the copy's internal conditions read the copied
 * fields, not the originals. A ``visible_if`` clause that points OUTSIDE the subtree is
 * dropped, because the copy may land on a page the external controller no longer
 * precedes — keeping it would silently make the workflow unpublishable.
 */
function reconcileDuplicateSubtreeRefs(
  element: TIntakeTreeElement,
  nameMap: Map<string, string>,
): void {
  reconcileDuplicateConditionRefs(element, nameMap);
  if (element.kind === "expression") remapExpressionRefs(element.expression, nameMap);
  if (element.kind === "field_group") {
    for (const child of element.children) reconcileDuplicateSubtreeRefs(child, nameMap);
  } else if (element.kind === "columns") {
    for (const column of element.columns) {
      for (const child of column.children) reconcileDuplicateSubtreeRefs(child, nameMap);
    }
  }
}

const SYSTEM_FIELD_LABELS: Record<TIntakeSystemFieldKind, TIntakeLocalizedText> = {
  request_name: { en: "Request name", fr: "Nom de la demande" },
  requester: { en: "Requester", fr: "Demandeur" },
  request_owner: { en: "Request owner", fr: "Responsable de la demande" },
  request_reviewer: { en: "Request reviewer", fr: "Réviseur de la demande" },
  target_launch_date: { en: "Target launch date", fr: "Date de lancement cible" },
};

function makeSystemFieldElement(kind: TIntakeSystemFieldKind): TIntakeSystemFieldElement {
  // request_owner defaults to internal (hidden from the requester) and optional
  // (#2694): when no owner is submitted the request auto-assigns the workflow's
  // default request owner (form-level default_request_owner_ids). Mirrors the
  // backend new-form default in `_empty_definition`.
  const isOwner = kind === "request_owner";
  return {
    id: kind,
    kind: "system_field",
    system_field: kind,
    label: { ...SYSTEM_FIELD_LABELS[kind] },
    internal: isOwner,
    // OPTIONAL_SYSTEM_FIELD_KINDS, not `!isOwner`: the launch date is optional
    // too, and the backend enforces the split whatever this claims — so hardcoding
    // it here only ever published a definition asserting the wrong thing.
    required: !OPTIONAL_SYSTEM_FIELD_KINDS.has(kind),
    visible_if: null,
  };
}

/**
 * Inject any DEFAULT_SYSTEM_FIELD_KINDS missing from the definition at the top
 * of the first page, in canonical order. Idempotent.
 *
 * Mirrors the backend's ``inject_missing_default_system_fields`` so the builder
 * stays consistent even if a definition loads from a stale source (legacy form
 * created before the data migration ran, dev re-seed, etc.). Defense-in-depth.
 */
function injectMissingDefaultSystemFields(definition: TIntakeDefinition): TIntakeDefinition {
  if (!definition.pages?.length) return definition;
  const present = new Set<string>();
  for (const page of definition.pages) {
    for (const { element } of iterTree(page)) {
      if (element.kind === "system_field") present.add(element.system_field);
    }
  }
  const firstPage = definition.pages[0];
  if (!firstPage) return definition;
  let insertAt = 0;
  for (const kind of DEFAULT_SYSTEM_FIELD_KINDS) {
    if (present.has(kind)) continue;
    firstPage.elements.splice(insertAt, 0, makeSystemFieldElement(kind));
    insertAt += 1;
    present.add(kind);
  }
  return definition;
}

function pageContainsSystemField(page: TIntakePage): boolean {
  for (const { element } of iterTree(page)) {
    if (isSystemFieldElement(element)) return true;
  }
  return false;
}

// System fields are locked against delete/required/rename, but their position
// is fully reorderable — authors can drop new fields anywhere and rearrange
// system fields freely. Just clamp into [0, length].
function clampInsertIndex(page: TIntakePage, atIndex: number): number {
  return Math.max(0, Math.min(atIndex, page.elements.length));
}

type ChoiceElement = Extract<TIntakeElement, { options: TIntakeChoiceOption[] }>;

function isChoiceElement(el: TIntakeTreeElement | undefined): el is ChoiceElement {
  return el?.kind === "radio" || el?.kind === "checkboxes" || el?.kind === "select";
}

interface TIntakeFormTopLevel {
  name: string;
  /** Authored rich-text HTML. The plain `description` is derived server-side,
   *  so the builder only ever holds and sends this half (NG-352). */
  descriptionHtml: string;
  is_active: boolean;
  teams: string[];
  request_type: string;
  default_request_category_value_id: string | null;
  default_request_owner_ids: string[];
  /** Managing Team id (#3144) — the OWNER axis, single-select, so a scalar and
   *  not a list like ``teams`` (the Submitting audience). ``null`` is
   *  workspace-managed. */
  managingTeamId: string | null;
}

function emptyTopLevel(): TIntakeFormTopLevel {
  return {
    name: "",
    descriptionHtml: "",
    is_active: false,
    teams: [],
    request_type: "",
    default_request_category_value_id: null,
    default_request_owner_ids: [],
    managingTeamId: null,
  };
}

/** Must match the `tableId` the list surface passes to `<DataTable>` and to
 *  `useDataTableColumnPrefs` (`intake-submissions-list.tsx`) — the persisted
 *  sort/filters share that table's key namespace. */
const SUBMISSIONS_TABLE_ID = "intake-submissions";

/** Only the columns the BE allow-list can sort. Others are not sortable. */
const SUBMISSIONS_COLUMN_TO_ORDER_BY: Record<string, string> = {
  status: "status",
  requestedBy: "requested_by",
  formUsed: "form",
  created: "submitted_at",
  lastUpdated: "updated_at",
  // Urgency sort: most overdue first → ascending remaining → no-SLA last (BE).
  sla: "sla",
  // Priority sort: highest score first (desc) → nulls last (BE).
  priority: "priority",
  // Request Category sort: the BE orders on the category value's own `order`
  // (the sequence the settings editor defines), not its label, and puts
  // uncategorised requests last in both directions (NG-492).
  requestCategory: "category",
};

/**
 * Validates a stored submissions sort against the ids it may legitimately
 * hold — the map's own keys, so the two cannot drift (NG-629).
 *
 * Derived rather than written out because that map is already this surface's
 * definition of "sortable": `intake-submissions-columns.tsx` marks exactly
 * these columns `enableSorting: true`, and an id outside it resolves to the
 * empty token, i.e. no server sort at all. Before this, a stored id left behind
 * by a renamed column passed shape validation, suppressed the default sort, and
 * sat in `submissionsSorting` where no header could clear it.
 *
 * Built once at module scope rather than per hydration, so re-entering the list
 * does not rebuild the Set behind it.
 */
const isSubmissionsSorting = isSortingStateOver(Object.keys(SUBMISSIONS_COLUMN_TO_ORDER_BY));

/** What a list with no Display settings of its own shows. */
const DEFAULT_SUBMISSIONS_DISPLAY: SubmissionsDisplay = { groupBy: "none", archivedView: "active" };

/**
 * Append-to-the-end insertion index. ``insertElementAtPath`` clamps into the
 * target container, so an out-of-range index lands at its tail — the same idiom
 * the drag handlers use for a container/root tail drop.
 */
const TAIL = Number.MAX_SAFE_INTEGER;

/** One row picked in the builder's "Add Global Field" modal (NG-689). The two
 *  singletons have no bank record, so they cannot be addressed by field id. */
export type TGlobalFieldPick =
  | { type: "program" }
  | { type: "goal" }
  | { type: "request_category" }
  // Parameterised rather than a `{type:"target_launch_date"}` arm, so a future
  // addable system field needs a picker row and nothing here. Note this is NOT
  // a route back for `request_owner`: a July 2026 product decision removed its
  // add affordance deliberately (it stays removable, never addable).
  | { type: "system_field"; systemField: TIntakeSystemFieldKind }
  // The field itself, not its id: the bank is a cached read the builder holds
  // (`useCustomFieldBank`, NG-2802), not store state this store can look up.
  | { type: "bank"; field: TWorkspaceCustomField };

export class IntakeStore {
  rootStore: RootStore;
  activeFormId: string | null = null;
  /**
   * The builder draft's baseline: the workflow as it was seeded into the
   * draft (`openBuilderForm`) or last written back. Not a cache of the
   * workflow: readers read the builder's cached read (`useBuilderForm`), and
   * `can_edit` lives only there. The writers diff against it and revert to it.
   */
  builderForm: TIntakeForm | null = null;
  /** What seeded the open workflow's draft: a cached list row while its detail
   *  read was in flight, the detail read's answer, or nothing yet (NG-3051). */
  private builderSeed: "row" | "detail" | null = null;
  /** The form being filled in the requester flow (`/intake/requests/:formId`).
   *  Set by IntakeRequesterFlow so the primary breadcrumb can label the crumb
   *  with the form name — the requester flow owns its forms in local state, not
   *  `forms`, so this is the breadcrumb's only handle on the active form. */
  activeRequestForm: { id: string; name: string } | null = null;
  activePageId: string | null = null;
  selectedElementId: string | null = null;
  /**
   * Container ids the author has folded away **on the builder canvas** (NG-1087).
   *
   * INVARIANT: builder view state — never serialised into the definition, never
   * read by the requester renderer. The requester-facing default is a different
   * field, `TIntakeFieldGroupElement.collapsed`, which documents why the two must
   * stay apart.
   *
   * Nothing resets it, deliberately. Element ids are unique across forms, so a
   * fold cannot leak between them, and the set therefore needs no hook in
   * `hydrateFromForm` — which is what lets a fold survive the autosave that
   * replaces the draft ~1s after every edit, and lets it survive navigating away
   * and back. It holds one id per container the author folded this session, so
   * ids of deleted elements are harmless: nothing looks them up.
   */
  builderCollapsedContainerIds = observable.set<string>();
  /** Transient "reveal this element" signal (#2518): bumping the nonce tells the
   *  element's field row to scroll into view + flash a destructive ring. Nonce so
   *  re-requesting the same id re-fires the reveal. */
  elementFlash: { elementId: string | null; nonce: number } = { elementId: null, nonce: 0 };
  /**
   * Where the global-field picker puts what it adds (NG-853).
   *
   * Held here rather than in the palette's local state because every container
   * on the canvas can open the one mounted modal, and each has to name its own
   * destination: the palette's button means the page root (``[]``), a column or
   * field-group button means that container's path. Do not render a second modal
   * per container — they would fight over this one target.
   *
   * INVARIANT: this OUTLIVES the picker closing, which is why it is separate
   * from ``globalFieldPickerOpen`` rather than doubling as it. The create-a-
   * field sub-flow closes the picker *before* opening the create modal
   * (`global-field-picker-dialog.tsx`), so a field created from inside a column
   * is added after the close — collapse the two and that add silently lands at
   * the page root, which is the very bug NG-853 fixes.
   */
  globalFieldPickerTarget: { pageIndex: number; path: ContainerPath } = { pageIndex: 0, path: [] };
  /** Whether the global-field picker is showing. Paired with, but deliberately
   *  not folded into, ``globalFieldPickerTarget`` — see the invariant there. */
  globalFieldPickerOpen = false;
  /** Which locale the builder's properties pane edits — the EN/FR inputs
   *  collapsed into a single field driven by this switch. */
  builderLocale: TIntakeLocale = "en";
  /**
   * Choice-option labels only: show EN and FR side by side instead of just
   * ``builderLocale`` (NG-444). Off by default — two half-width inputs per row
   * truncate a real-length label, which is the readability problem this fixes.
   * On the store rather than in the OptionsEditor so it survives selecting a
   * different element; as component state a translator re-checked it per field.
   */
  showBothOptionLocales = false;
  draft: TIntakeDefinition | null = null;
  topLevel: TIntakeFormTopLevel = emptyTopLevel();
  isSaving = false;
  isCreating = false;
  isPublishing = false;
  /** ISO timestamp of the last successful autosave; ``null`` if the form
   *  is fully published with no pending draft. Drives "Saved Xs ago". */
  draftUpdatedAt: string | null = null;
  /** ISO timestamp of the last successful Publish. */
  publishedAt: string | null = null;
  hasUnpublishedChanges = false;
  inlineErrors: BuilderError[] = [];
  bannerErrors: string[] = [];
  /** Requests the viewer moved to a Home Team that took their own access away,
   *  keyed by id, holding the team's name (NG-3182): the detail page says so in
   *  place of the request, which it can no longer read. */
  movedOutOfReach: Map<string, string> = new Map();
  // Server-side filter state, one field per BE filter param on
  // ``IntakeSubmissionListEndpoint``. The list page's column-header filters
  // call the matching ``setSubmissions...`` setter, which assigns the value
  // and then re-fetches the first page so the cursor window matches the new
  // filter set.
  submissionsFormFilter: string[] = [];
  submissionsProgramFilter: string[] = [];
  /** The request's Home team, matched exactly (NG-3462): ``home_team_ids``. */
  submissionsHomeTeamFilter: string[] = [];
  /** Whether the Home team column and filter are offered: the list mirrors the
   *  `request-home-team` kill switch here, so with it off a remembered Home team
   *  filter neither narrows the list nor counts as an applied filter. */
  submissionsHomeTeamEnabled = true;
  submissionsSearch = "";
  submissionsStatusFilter: string[] = [];
  submissionsRequestedByFilter: string[] = [];
  submissionsOwnerFilter: string[] = [];
  /** Multi-id managed Request Category filter (NG-421). Matches the request's
   *  ``category_value_id``, so a legacy request showing a request-type fallback
   *  label is not selected by the category of the same name. */
  submissionsCategoryFilter: string[] = [];
  /** Multi-state SLA adherence filter (within/paused/breached/met). */
  submissionsSlaFilter: string[] = [];
  /** Multi-tier priority filter (urgent/high/medium/low). */
  submissionsPriorityFilter: string[] = [];
  submissionsCreatedAfter = "";
  submissionsCreatedBefore = "";
  submissionsUpdatedAfter = "";
  submissionsUpdatedBefore = "";
  /** Active (default, hides archived) | archived (only archived) | all. */
  submissionsArchivedView: TIntakeSubmissionsArchivedView = "active";
  // DataTable bridge: TanStack-shaped sort/selection state. ``submissionsOrderBy``
  // is the resolved server token derived from ``submissionsSorting`` and sent on
  // the list query. ``submissionsColumnFilters`` (getter below) bridges the flat
  // filter fields above to the TanStack ColumnFiltersState the primitive consumes.
  submissionsSorting: SortingState = [];
  submissionsOrderBy = "";
  submissionsRowSelection: RowSelectionState = {};
  // Server-side group-by for the table view (#1470). When set to a dimension,
  // the list fetch sends ``group_by`` and the endpoint orders the page by that
  // field so each group's rows are contiguous; the client renders a group
  // header when the key changes. The board view groups by status inherently
  // and ignores this. The list's pages carry the server's complete per-group
  // counts (#1524), so headers show true totals, not the loaded window.
  submissionsGroupBy: SubmissionGroupBy = "none";
  // The workspace whose stored list prefs are currently loaded, and therefore
  // the only key `persistSubmissionsPref` may write.
  //
  // Deliberately NOT `rootStore.router.workspaceSlug`: `RouterStore.params` is
  // set from an effect, so during a workspace switch the router still holds the
  // OUTGOING slug while the list is already mounting for the incoming one —
  // which would file the incoming workspace's filters under the outgoing
  // workspace's key. The slug hydration was called with comes straight from the
  // route param the surface renders for, so it cannot lag.
  //
  // Not observable: bookkeeping about a read, nothing renders it.
  private submissionsPrefsSlug: string | null = null;
  // True only while `hydrateSubmissionsPrefs` is applying a value it just READ,
  // so the persistence reactions below don't write it straight back.
  private hydratingSubmissionsPrefs = false;
  /**
   * Tracked as a flag (set on every mutation, cleared on hydrate/save) instead of computed
   * via JSON.stringify(draft), which would re-serialize the whole definition on every
   * keystroke in the properties pane.
   */
  private _dirty = false;
  /** In-flight PATCH so ``flushPendingSave`` can await a debounce that already
   *  fired, instead of overlapping a second write. Not observable — a Promise
   *  is not a value the UI should react to. */
  private saveInFlight: Promise<void> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        submissionsPrefsSlug: false,
        hydratingSubmissionsPrefs: false,
        // NOT an action, deliberately — see `hydrateSubmissionsPrefs`. MobX
        // flushes pending reactions when the OUTERMOST action ends, so if this
        // method were an action its own `runInAction` would not flush them and
        // the write-back guard inside it would already have been cleared by the
        // time the persistence reactions ran.
        hydrateSubmissionsPrefs: false,
      } as never,
      { autoBind: true },
    );
    // Opting out of the action wrapping above also opts out of `autoBind`, and
    // store methods are routinely lifted off the instance in this codebase.
    // Bind it back so that stays safe.
    this.hydrateSubmissionsPrefs = this.hydrateSubmissionsPrefs.bind(this);
    this.watchSubmissionsListPrefs();
  }

  // ---- Persisted list preferences (NG-517) ----

  /**
   * The ONE place the submissions list sort + filters are persisted.
   *
   * Deliberately a reaction over the DERIVED state rather than a write in each
   * setter. Thirteen mutators write the state these two keys hold — the twelve
   * flat facet setters behind the board's Filter popover and
   * `setSubmissionsColumnFilters` — so wiring the write to one of them would
   * leave storage diverging from state: clearing a facet the facade never saw
   * would leave the stored entry behind and resurrect it on the next reload.
   * Reacting to the derived value covers every mutator, including the next one
   * someone adds.
   *
   * `submissionsColumnFilters` is the SAME value the surface hands to
   * `<DataTable>`, so what gets stored is the whole set the user sees.
   *
   * No disposer: `IntakeStore` lives for the lifetime of the app, so these
   * reactions are never torn down.
   */
  private watchSubmissionsListPrefs(): void {
    reaction(
      () => this.submissionsColumnFilters,
      (filters) => this.persistSubmissionsPref("columnFilters", filters),
      // Structural, so re-deriving an equal set is not a write — the getter
      // builds a fresh array on every evaluation.
      { equals: comparer.structural },
    );
    reaction(
      () => [...this.submissionsSorting],
      (sorting) => this.persistSubmissionsPref("sorting", sorting),
      { equals: comparer.structural },
    );
  }

  private submissionsPrefsKey(slug: string): TablePrefsKey {
    // `|| null` is a second line of defence — `persistSubmissionsPref` and
    // `hydrateSubmissionsPrefs` both bail on a falsy slug. An empty-string
    // scope collapses to the UNSCOPED key, which collides with the legacy
    // column-prefs namespace and is orphaned the moment a real slug arrives.
    return { tableId: SUBMISSIONS_TABLE_ID, scope: slug || null };
  }

  private persistSubmissionsPref(
    slice: "sorting" | "columnFilters",
    value: SortingState | ColumnFiltersState,
  ): void {
    // Behind `persisted-table-prefs` (NG-517) — this store reaches
    // `writeTablePref` directly, so the hook-side gate does not cover it.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    // Hydration applies a value it just READ; writing it back would be a
    // pointless write and would misreport who last wrote the key.
    if (this.hydratingSubmissionsPrefs) return;
    // No hydrated workspace means we do not know whose key this is. That is
    // only reachable before the list has mounted, where there is nothing worth
    // persisting anyway.
    if (!this.submissionsPrefsSlug) return;
    writeTablePref(this.submissionsPrefsKey(this.submissionsPrefsSlug), slice, value);
  }

  /**
   * Reset the submissions list's session state and restore this workspace's
   * stored sort + filters. `display` is the Group by and Show archived the list
   * opens with: the caller reads them from the URL, which has already been
   * filled from the remembered settings on a bare visit.
   *
   * Called as the FIRST statement of the list's mount / workspace-change effect,
   * which then enables the list's read — so that one request already carries
   * the restored sort and filters instead of an unfiltered request being
   * followed by a filtered one. (There is no `clientLoader` here: the read is
   * enabled from that effect, and the effect is not keyed on the filter state,
   * so hydrating at the top of it cannot re-fire it.)
   *
   * The reset is folded IN rather than left to the caller on purpose. It used
   * to be a bare `clearSubmissionsFilters()` in that effect; run before
   * hydration from outside the write-back guard, the cleared value would reach
   * the persistence reaction and wipe the very key hydration is about to read.
   *
   * Idempotent: it derives the whole persisted state from storage, so calling it
   * twice for the same slug lands on the same state — and re-entering a
   * workspace re-reads that workspace's key rather than carrying the previous
   * one's entity ids.
   */
  hydrateSubmissionsPrefs(
    slug: string,
    display: SubmissionsDisplay = DEFAULT_SUBMISSIONS_DISPLAY,
  ): void {
    if (!slug) return;
    const key = this.submissionsPrefsKey(slug);
    // Behind `persisted-table-prefs` (NG-517). Gated at the READS rather than
    // with an early return, because this method also owns the session RESET
    // folded in above — bailing outright would leave one workspace's filters
    // applied after a switch to another, which is a bug the flag must not be
    // able to cause. Restoring nothing is exactly the pre-NG-517 behaviour: the
    // reset still runs, and the empty arrays below are what it used to apply.
    const restore = tablePrefsEnabledNow(this.rootStore.featureFlags);
    // `readTablePref` distinguishes absent from empty, so a user who cleared
    // the sort gets `[]` back rather than a default.
    const sorting = restore
      ? readTablePref<SortingState>(key, "sorting", [], isSubmissionsSorting)
      : [];
    const filters = restore
      ? readTablePref<ColumnFiltersState>(key, "columnFilters", [], isColumnFiltersState)
      : [];
    // The guard has to still be set when the persistence reactions run. MobX
    // flushes them as the OUTERMOST action ends — which is why this method is
    // excluded from `makeAutoObservable`'s action wrapping (see the
    // constructor): that makes the `runInAction` below the outermost action, so
    // the reactions fire inside it, while the guard is up. All observable writes
    // here must therefore stay inside that `runInAction`.
    this.hydratingSubmissionsPrefs = true;
    try {
      runInAction(() => {
        this.submissionsPrefsSlug = slug;
        this.clearSubmissionsFilters();
        this.applySubmissionsSorting(sorting);
        this.applySubmissionsColumnFilters(filters);
        this.applySubmissionsDisplay(display);
      });
    } finally {
      this.hydratingSubmissionsPrefs = false;
    }
  }

  /** Apply Display settings during hydration. Assigns the fields directly
   *  rather than through the setters, which hydration has no need of. */
  private applySubmissionsDisplay(display: SubmissionsDisplay): void {
    this.submissionsGroupBy = display.groupBy;
    this.submissionsArchivedView = display.archivedView;
  }

  /**
   * Bring the list's Display settings in line with the URL's after it changes
   * under a mounted list: a Display menu pick, or a link to the list it is on.
   * A write (so a new list key) when either setting moved, none when both
   * already match, so the list's own restore write on mount costs no second
   * request.
   */
  syncSubmissionsDisplay(display: SubmissionsDisplay): void {
    if (
      this.submissionsGroupBy === display.groupBy &&
      this.submissionsArchivedView === display.archivedView
    ) {
      return;
    }
    this.submissionsGroupBy = display.groupBy;
    this.submissionsArchivedView = display.archivedView;
  }

  setActiveRequestForm(form: { id: string; name: string } | null): void {
    this.activeRequestForm = form;
  }

  /** The open workflow's draft baseline (see `builderForm`), or `null`. */
  get form(): TIntakeForm | null {
    if (!this.activeFormId) return null;
    return this.builderForm?.id === this.activeFormId ? this.builderForm : null;
  }

  get isDirty(): boolean {
    if (!this.form || !this.draft) return false;
    return this._dirty;
  }

  /**
   * Client-side preflight errors derived from the current draft. Computed
   * lazily in the property-pane render path (no store mutation), which keeps
   * the existing post-400 ``inlineErrors`` plumbing untouched — both sources
   * are merged via ``allInlineErrors`` so the inline-error chip renderer
   * doesn't have to care where an error came from.
   */
  get clientInlineErrors(): BuilderError[] {
    return validateDraft(this.draft);
  }

  /** Merged backend (post-400) + client-side preflight inline errors. */
  get allInlineErrors(): BuilderError[] {
    return [...this.clientInlineErrors, ...this.inlineErrors];
  }

  /**
   * True when the draft passes client-side preflight. Wired to the Save button's
   * ``disabled`` prop so authors can't fire a request that the backend will
   * reject for the well-known set of required-field omissions. Backend remains
   * the source of truth — this is a UX accelerator.
   */
  get isDraftValid(): boolean {
    if (!this.draft) return false;
    return this.clientInlineErrors.length === 0;
  }

  /** Why the Publish button should be disabled (or null when enabled).
   *  Mirrors the PRD gates so the FE tooltip + the BE 409/422 stay in
   *  sync: the form must be Active, name + default request category must be
   *  set, and the draft must be valid + actually contain pending changes.
   *
   *  The default Request Owner is deliberately NOT checked here (#3643). It is
   *  only required when the workflow can't assign an owner itself, and whether
   *  every route through the graph reaches a Change Owner node is a server-side
   *  question — re-deriving it client-side would duplicate a graph walk and go
   *  stale the moment the rule changes. A form that genuinely still needs a
   *  default gets the BE's 422, which the publish handler already surfaces by
   *  name. */
  get publishDisabledReason():
    | "no_changes"
    | "inactive"
    | "missing_required"
    | "invalid_draft"
    | null {
    if (!this.form) return "no_changes";
    if (!this.hasUnpublishedChanges) return "no_changes";
    if (!this.topLevel.is_active) return "inactive";
    if (!this.topLevel.name.trim() || !this.topLevel.default_request_category_value_id) {
      return "missing_required";
    }
    if (!this.isDraftValid) return "invalid_draft";
    return null;
  }

  get canPublish(): boolean {
    return this.publishDisabledReason === null;
  }

  /** Refresh only server-owned metadata, leaving the editable draft/topLevel
   *  and the dirty flag untouched. Used after an autosave that returned while
   *  the user was still editing, so the stale echo can't clobber newer edits. */
  private applyServerMeta(form: TIntakeForm): void {
    this.draftUpdatedAt = form.draft_updated_at ?? null;
    this.publishedAt = form.published_at ?? null;
    this.hasUnpublishedChanges = Boolean(form.has_unpublished_changes);
  }

  private hydrateFromForm(form: TIntakeForm): void {
    this.activeFormId = form.id;
    this.builderForm = form;
    this.draft = injectMissingDefaultSystemFields(clone(form.definition));
    this.topLevel = {
      name: form.name,
      descriptionHtml: form.description_html,
      is_active: form.is_active,
      teams: form.teams ?? [],
      request_type: form.request_type ?? "",
      default_request_category_value_id: form.default_request_category_value_id ?? null,
      default_request_owner_ids: form.default_request_owner_ids ?? [],
      // ``managing_team_id`` already carries the draft overlay when the form was
      // fetched with ``include_draft``; ``managing_team`` is the same value with
      // the name attached, so it is the fallback for a response that only
      // serialized the object form.
      managingTeamId: form.managing_team_id ?? form.managing_team?.id ?? null,
    };
    // Author meta — undefined for non-author responses, so default to safe.
    this.draftUpdatedAt = form.draft_updated_at ?? null;
    this.publishedAt = form.published_at ?? null;
    this.hasUnpublishedChanges = Boolean(form.has_unpublished_changes);
    // Preserve activePageId across re-hydration (e.g. after save) when the page
    // still exists. Without this, saving while editing a non-first page would
    // snap the builder back to page 1. Falls back to the first page on initial
    // load or when the active page was removed server-side.
    const previousActivePageId = this.activePageId;
    const pageStillExists =
      previousActivePageId !== null &&
      form.definition.pages.some((p) => p.id === previousActivePageId);
    this.activePageId = pageStillExists
      ? previousActivePageId
      : (form.definition.pages[0]?.id ?? null);
    // Preserve selectedElementId for the same reason: with autosave firing
    // every ~1s after a drop, clearing the selection on every PATCH return
    // makes the right-hand config pane flicker and snap shut while the
    // user is still editing the new element. Keep the selection when the
    // element still lives somewhere in the new draft tree.
    const previousSelectedId = this.selectedElementId;
    const selectedStillExists =
      previousSelectedId !== null &&
      this.draft !== null &&
      this.draft.pages.some((p) => findElementById(p, previousSelectedId) !== null);
    this.selectedElementId = selectedStillExists ? previousSelectedId : null;
    this._dirty = false;
    this.clearErrors();
  }

  setActivePage(pageId: string): void {
    if (!this.draft) return;
    if (this.draft.pages.some((p) => p.id === pageId)) {
      this.activePageId = pageId;
      this.selectedElementId = null;
    }
  }

  setBuilderLocale(locale: TIntakeLocale): void {
    this.builderLocale = locale;
  }

  /** Is this container folded away on the builder canvas? */
  isBuilderContainerCollapsed(containerId: string): boolean {
    return this.builderCollapsedContainerIds.has(containerId);
  }

  /** Fold / unfold a container on the builder canvas. Writes nothing to the
   *  definition, so it can never disturb "Collapsed by default". */
  toggleBuilderContainerCollapsed(containerId: string): void {
    if (this.builderCollapsedContainerIds.has(containerId)) {
      this.builderCollapsedContainerIds.delete(containerId);
    } else {
      this.builderCollapsedContainerIds.add(containerId);
    }
  }

  setShowBothOptionLocales(showBoth: boolean): void {
    this.showBothOptionLocales = showBoth;
  }

  selectElement(id: string | null): void {
    if (id === null) {
      this.selectedElementId = null;
      return;
    }
    if (!this.draft) return;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return;
    if (findElementById(activePage, id)) {
      this.selectedElementId = id;
    }
  }

  /** Select an element AND ask its field row to scroll into view + flash — used
   *  by the Edit Page modal's blank-option banner to reveal the offending field
   *  even when the field list is long. */
  revealElement(id: string): void {
    this.selectElement(id);
    this.elementFlash = { elementId: id, nonce: this.elementFlash.nonce + 1 };
  }

  get selectedElement(): TIntakeTreeElement | null {
    if (!this.draft || !this.selectedElementId) return null;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return null;
    const found = findElementById(activePage, this.selectedElementId);
    return found?.element ?? null;
  }

  /**
   * Location (parent path + index) for the currently selected element, walking
   * the page tree. ``null`` when nothing is selected or the id has dropped off
   * the tree (e.g. parent container was deleted).
   */
  get selectedElementLocation(): { path: ContainerPath; index: number } | null {
    if (!this.draft || !this.selectedElementId) return null;
    const activePage = this.draft.pages.find((p) => p.id === this.activePageId);
    if (!activePage) return null;
    const found = findElementById(activePage, this.selectedElementId);
    return found?.location ?? null;
  }

  /**
   * @deprecated Top-level-index accessor retained for back-compat with the
   * flat properties-pane API. Tree-aware callers should use
   * ``selectedElementLocation`` instead.
   */
  get selectedElementIndex(): number | null {
    const loc = this.selectedElementLocation;
    if (!loc) return null;
    return loc.path.length === 0 ? loc.index : null;
  }

  /** ``managingTeamId`` seeds the new workflow's owner (#3144). It is resolved
   *  by the caller (which knows *where* the create was started from) and only
   *  sent when it is known — never guessed, so a workflow whose owner is
   *  ambiguous is born workspace-managed and the author picks one. */
  async createForm(
    slug: string,
    name?: string,
    options: { managingTeamId?: string | null } = {},
    source: AnalyticsSource = "page",
  ): Promise<TIntakeForm | null> {
    this.isCreating = true;
    try {
      const created = await this.rootStore.intakeService.createForm(slug, {
        name: name?.trim() || "Untitled workflow",
        ...(options.managingTeamId ? { managing_team_id: options.managingTeamId } : {}),
      });
      runInAction(() => {
        this.hydrateFromForm(created);
      });
      track("form_created", { form_id: created.id, source });
      this.markFormsStale(slug);
      return created;
    } catch (err) {
      // Shown to the author as a banner; logged so the failure has its context.
      console.error("intake: could not create a workflow", { slug, err });
      runInAction(() => {
        this.bannerErrors = [(err as Error).message];
      });
      return null;
    } finally {
      runInAction(() => {
        this.isCreating = false;
      });
    }
  }

  /** Bulk-import requests into a form from parsed CSV rows (NG-784). Returns the
   *  per-row report. The caller (the import wizard) owns retry/summary UX, so
   *  this only makes the call and records analytics. */
  async importForm(
    slug: string,
    formId: string,
    body: TRequestImportRequest,
  ): Promise<TRequestImportResult> {
    const report = await this.rootStore.intakeService.importForm(slug, formId, body);
    // The imported requests belong in the cached requests lists (NG-2808).
    const client = peekQueryClient();
    if (client && report.created_count > 0) markCachedRequestListsStale(client, slug);
    track("requests_bulk_imported", {
      created: report.created_count,
      errors: report.error_count,
      total: report.total,
    });
    // The request's own create event too, so request counts include imports
    // (NG-2057): one event for the whole batch, carrying how many it created.
    if (report.created_count > 0) {
      track("intake_request_submitted", {
        form_id: formId,
        count: report.created_count,
        source: "import",
      });
    }
    return report;
  }

  // Every topLevel setter hands back a NEW object (like ``draft``) rather than
  // mutating in place. The autosave effect (forms-edit.tsx) keys its debounce
  // timer on ``topLevel`` identity; an in-place mutation kept the reference
  // stable, so the effect never re-ran to reset the timer — collapsing the 1s
  // debounce into a 1s throttle that fired mid-keystroke and dropped letters.
  setFormName(name: string): void {
    this.topLevel = { ...this.topLevel, name };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDescriptionHtml(descriptionHtml: string): void {
    this.topLevel = { ...this.topLevel, descriptionHtml };
    this._dirty = true;
    this.clearErrors();
  }

  setFormTeams(teamIds: string[]): void {
    this.topLevel = { ...this.topLevel, teams: [...teamIds] };
    this._dirty = true;
    this.clearErrors();
  }

  /** Managing Team — the ownership axis (#3144). ``null`` clears the pending
   *  draft change; it does not make a published workflow workspace-managed. */
  setFormManagingTeam(teamId: string | null): void {
    this.topLevel = { ...this.topLevel, managingTeamId: teamId };
    this._dirty = true;
    this.clearErrors();
  }

  setFormRequestType(requestType: string): void {
    this.topLevel = { ...this.topLevel, request_type: requestType };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDefaultRequestCategory(valueId: string | null): void {
    this.topLevel = { ...this.topLevel, default_request_category_value_id: valueId };
    this._dirty = true;
    this.clearErrors();
  }

  setFormDefaultRequestOwners(ownerIds: string[]): void {
    this.topLevel = { ...this.topLevel, default_request_owner_ids: [...ownerIds] };
    this._dirty = true;
    this.clearErrors();
  }

  applyDefinitionPatch(updater: (draft: TIntakeDefinition) => void): void {
    if (!this.draft) return;
    const next = clone(this.draft);
    updater(next);
    this.draft = next;
    if (this.selectedElementId) {
      const activePage = next.pages.find((p) => p.id === this.activePageId);
      const stillThere = activePage ? findElementById(activePage, this.selectedElementId) : null;
      if (!stillThere) this.selectedElementId = null;
    }
    this._dirty = true;
    this.clearErrors();
  }

  clearErrors(): void {
    this.inlineErrors = [];
    this.bannerErrors = [];
  }

  discard(): void {
    if (!this.form) return;
    const previousActivePageId = this.activePageId;
    this.hydrateFromForm(this.form);
    if (previousActivePageId && this.draft?.pages.some((p) => p.id === previousActivePageId)) {
      this.activePageId = previousActivePageId;
    }
  }

  addPage(): void {
    const newPage = makeNewPage();
    this.applyDefinitionPatch((draft) => {
      draft.pages.push(newPage);
    });
    this.activePageId = newPage.id; // jump to the new tab so the user sees what they added
  }

  removePage(pageIndex: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page || pageContainsSystemField(page)) return;
    const wasActive = this.activePageId === page.id;
    this.applyDefinitionPatch((draft) => {
      draft.pages.splice(pageIndex, 1);
    });
    if (wasActive && this.draft) {
      // Fall back to the page now occupying the removed index, else the last page.
      const fallback = this.draft.pages[Math.min(pageIndex, this.draft.pages.length - 1)] ?? null;
      this.activePageId = fallback?.id ?? null;
    }
  }

  movePage(fromIndex: number, toIndex: number): void {
    if (!this.draft) return;
    if (fromIndex === toIndex) return;
    const source = this.draft.pages[fromIndex];
    if (!source) return;
    if (pageContainsSystemField(source) && toIndex !== 0) return;
    const target = this.draft.pages[toIndex] ?? this.draft.pages[this.draft.pages.length - 1];
    if (target && pageContainsSystemField(target) && fromIndex !== 0) return;
    this.applyDefinitionPatch((draft) => {
      const [moved] = draft.pages.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, draft.pages.length));
      draft.pages.splice(insertAt, 0, moved);
    });
  }

  setPageName(pageIndex: number, locale: "en" | "fr", value: string): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.name[locale] = value;
    });
  }

  setPageInternal(pageIndex: number, value: boolean): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.internal = value;
    });
  }

  setPageVisibleIf(pageIndex: number, condition: TIntakePage["visible_if"]): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (page) page.visible_if = condition;
    });
  }

  /**
   * Insert ``element`` into the children array addressed by ``path`` at
   * ``atIndex``. When ``path`` is ``[]`` the insertion respects the
   * system-field clamp (system fields always stay at the top of page 1).
   */
  insertElementAtPath(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    element: TIntakeTreeElement,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const target = getChildrenAtPath(page, path);
    if (!target) return;
    const clamped =
      path.length === 0
        ? clampInsertIndex(page, atIndex)
        : Math.max(0, Math.min(atIndex, target.length));
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const list = getChildrenAtPath(draftPage, path);
      if (!list) return;
      list.splice(clamped, 0, element);
    });
    this.selectElement(element.id);
  }

  /** Remove the element at ``path[index]``. No-op for locked (default) system
   *  fields; the placeable request_owner (#2948) is removable (#3089). */
  removeElementAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const el = list?.[index];
    if (!el || isLockedSystemField(el)) return;
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      if (!target) return;
      target.splice(index, 1);
    });
  }

  /**
   * Move an element between two paths within the same page. Cycles are
   * rejected (cannot drop a container into its own subtree).
   */
  moveElementBetweenPaths(
    pageIndex: number,
    fromPath: ContainerPath,
    fromIndex: number,
    toPath: ContainerPath,
    toIndex: number,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const fromList = getChildrenAtPath(page, fromPath);
    const source = fromList?.[fromIndex];
    if (!source) return;
    // System fields are locked against delete/required/rename, but their
    // position is reorderable like any other element.

    // Reparent cycle guard — refuse to drop a container into its own subtree.
    if (source.kind === "field_group" || source.kind === "columns") {
      const forbidden = collectSubtreeIds(source);
      if (pathTouches(toPath, forbidden)) return;
    }

    if (pathEquals(fromPath, toPath) && fromIndex === toIndex) return;

    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const draftFrom = getChildrenAtPath(draftPage, fromPath);
      if (!draftFrom) return;
      const [moved] = draftFrom.splice(fromIndex, 1);
      if (!moved) return;
      const draftTo = getChildrenAtPath(draftPage, toPath);
      if (!draftTo) {
        // Path disappeared mid-mutation (shouldn't happen, but restore).
        draftFrom.splice(fromIndex, 0, moved);
        return;
      }
      // ``toIndex`` is already a post-removal insertion index — the DnD layer's
      // ``computeDropIndex`` compensates for the dragged element shifting its
      // siblings down on a same-list move. Re-applying that ``fromIndex < toIndex``
      // decrement here double-counted it, so downward drags landed one slot short
      // (a one-row move became a no-op). Insert at ``toIndex`` directly, matching
      // ``movePage`` / ``moveOptionAtPath``.
      const clamped =
        toPath.length === 0
          ? clampInsertIndex(draftPage, toIndex)
          : Math.max(0, Math.min(toIndex, draftTo.length));
      draftTo.splice(clamped, 0, moved);
    });
  }

  addElement(pageIndex: number, kind: NewElementKind): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    this.insertElementAtPath(pageIndex, [], page.elements.length, makeNewElement(kind));
  }

  /** Add a layout element (info_text / divider / field_group / columns). */
  addLayoutElement(pageIndex: number, kind: LayoutPaletteKind): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    this.insertElementAtPath(pageIndex, [], page.elements.length, makeLayoutElement(kind));
  }

  /** Add a layout element at a specific path + index. */
  insertLayoutElement(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    kind: LayoutPaletteKind,
  ): void {
    this.insertElementAtPath(pageIndex, path, atIndex, makeLayoutElement(kind));
  }

  /** ``path`` is second and required, like the singleton adders below — an
   *  optional destination defaulting to the page root is precisely the
   *  assumption NG-853 had to undo. */
  addElementFromBankField(
    pageIndex: number,
    path: ContainerPath,
    field: TWorkspaceCustomField,
  ): void {
    this.insertElementAtPath(pageIndex, path, TAIL, makeNewElementFromBankField(field));
  }

  /** True iff the draft already holds ``kind`` anywhere — any page, any nesting
   *  depth. Deliberately draft-wide, so the guard is unaffected by which
   *  container a new one was aimed at. */
  hasSingletonKind(kind: TSingletonElementKind): boolean {
    for (const page of this.draft?.pages ?? []) {
      for (const { element } of iterTree(page)) {
        if (element.kind === kind) return true;
      }
    }
    return false;
  }

  /** Whether any page already carries this system field. A placed system field is
   *  one-per-FORM, not one-per-page: its answer key is the field's code, so a
   *  second element anywhere would give one key two competing answers. The
   *  backend rejects that on field-name uniqueness; this is what stops the
   *  picker offering the add in the first place. */
  hasSystemFieldElement(kind: TIntakeSystemFieldKind): boolean {
    for (const page of this.draft?.pages ?? []) {
      for (const { element } of iterTree(page)) {
        if (element.kind === "system_field" && element.system_field === kind) return true;
      }
    }
    return false;
  }

  /** ``path`` is second and required, like the other add paths — an optional
   *  destination defaulting to the page root is the assumption NG-853 undid.
   *  A launch date picked from inside a column belongs in that column. */
  addSystemFieldElement(
    pageIndex: number,
    path: ContainerPath,
    kind: TIntakeSystemFieldKind,
  ): void {
    if (this.hasSystemFieldElement(kind)) return; // one per form
    // A MINTED id, overriding `makeSystemFieldElement`'s `id: kind`. That
    // code-as-id is the canonical legacy-`definition` shape and is right for the
    // injection path, but a placed element is persisted as a `PageElement` whose
    // primary key is a UUID — `definition_to_page_elements` passes the id through
    // verbatim, so `id: "target_launch_date"` makes the page save 400.
    //
    // Safe for the answer: a system field's `response_data` key is its CODE, not
    // its id (`fieldName` on the FE, `_field_name` on the BE), so the id is
    // identity-only here. The one-per-form rule is likewise enforced on the code
    // via definition-wide field-name uniqueness, not on the id.
    this.insertElementAtPath(pageIndex, path, TAIL, {
      ...makeSystemFieldElement(kind),
      id: newId(),
    });
  }

  /**
   * Append a singleton element to the container at ``path``, or no-op when the
   * draft already holds one.
   *
   * ``path`` is second and required — an optional destination defaulting to the
   * page root is precisely the assumption NG-853 had to undo.
   */
  private addSingletonElement(
    pageIndex: number,
    path: ContainerPath,
    kind: TSingletonElementKind,
  ): void {
    if (this.hasSingletonKind(kind)) return;
    this.insertElementAtPath(pageIndex, path, TAIL, makeNewElement(kind));
  }

  hasProgramElement(): boolean {
    return this.hasSingletonKind("program");
  }

  addProgramElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "program");
  }

  hasGoalElement(): boolean {
    return this.hasSingletonKind("goal");
  }

  addGoalElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "goal");
  }

  hasRequestCategoryElement(): boolean {
    return this.hasSingletonKind("request_category");
  }

  addRequestCategoryElement(pageIndex: number, path: ContainerPath): void {
    this.addSingletonElement(pageIndex, path, "request_category");
  }

  /**
   * Append every picked global field to the container addressed by ``path`` in
   * one action, and reveal the first one (it can land below the fold). Backs the
   * builder's "Add Global Field" modal, which is the only way to add a global
   * field now that the palette no longer lists them (NG-689).
   *
   * ``path`` is the container the author asked for: ``[]`` for the palette's
   * button (append to the page), or a column / field-group path when the modal
   * was opened from inside that container. Before NG-853 this only ever
   * appended to the page root, so a global field meant for a column landed
   * beneath it and had to be dragged in by hand — the drag path it replaced
   * had honoured the container.
   *
   * Singleton picks route through ``addSingletonElement`` and a placed system
   * field through ``addSystemFieldElement``, so their one-per-form guards still
   * hold — a modal left open while the draft changed underneath cannot
   * double-add.
   */
  addGlobalFieldsAtPath(pageIndex: number, path: ContainerPath, picks: TGlobalFieldPick[]): void {
    if (!this.draft) return;
    let firstAddedId: string | null = null;
    for (const pick of picks) {
      if (pick.type === "bank") this.addElementFromBankField(pageIndex, path, pick.field);
      else if (pick.type === "system_field")
        this.addSystemFieldElement(pageIndex, path, pick.systemField);
      else this.addSingletonElement(pageIndex, path, pick.type);
      // Each successful insert selects the element it created; a guard that
      // refused the add leaves the previous selection in place, so only take
      // the id once and only when it actually moved.
      if (firstAddedId === null) firstAddedId = this.selectedElementId;
    }
    if (firstAddedId) this.revealElement(firstAddedId);
  }

  /**
   * Take every picked global field off the draft, wherever it sits — the
   * remove half of the builder's global-field modal (NG-689), which opens with
   * the attached fields ticked and treats unticking as removal.
   *
   * Scoped to the whole draft, not to one page: the modal lists what the form
   * holds, so a field placed on page 3 is untickable from page 1 and has to be
   * found across every page and every nested container. Each pick re-scans,
   * because removing one shifts the sibling indices of the next.
   *
   * Routed through ``removeElementAtPath`` so the locked-system-field guard and
   * the clone-and-replace patch invariant still hold. Like deleting from the
   * canvas, this does NOT rewrite a ``visible_if`` on another element that
   * referenced the removed field; the builder's error list is what surfaces the
   * dangling reference, and that behaviour is shared with the existing delete.
   */
  removeGlobalFieldsFromDraft(picks: TGlobalFieldPick[]): void {
    for (const pick of picks) {
      const found = this.locateGlobalField(pick);
      if (!found) continue;
      this.removeElementAtPath(found.pageIndex, found.path, found.index);
    }
  }

  /** Open the global-field picker aimed at one container. ``path`` is ``[]`` for
   *  the page root, which is what the palette's own button passes. Required, not
   *  defaulted: silently falling back to the root is the NG-853 bug. */
  openGlobalFieldPicker(pageIndex: number, path: ContainerPath): void {
    this.globalFieldPickerTarget = { pageIndex, path };
    this.globalFieldPickerOpen = true;
  }

  /** Closes the picker but KEEPS the target — see the invariant on it. */
  closeGlobalFieldPicker(): void {
    this.globalFieldPickerOpen = false;
  }

  /** The page index the builder is editing. Both a missing draft and a page id
   *  that no longer resolves fall back to the first page. */
  get activePageIndex(): number {
    const index = this.draft?.pages.findIndex((p) => p.id === this.activePageId);
    return index == null || index < 0 ? 0 : index;
  }

  /** Where a picked global field currently sits in the draft, or null. */
  private locateGlobalField(
    pick: TGlobalFieldPick,
  ): { pageIndex: number; path: ContainerPath; index: number } | null {
    const pages = this.draft?.pages ?? [];
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const page = pages[pageIndex];
      if (!page) continue;
      for (const { element, location } of iterTree(page)) {
        // `element.kind === pick.type` works for the singleton kinds because
        // their pick type IS the kind. A system-field pick is the exception: its
        // kind is the literal "system_field" and the code lives on the element,
        // so comparing against `pick.type` would never match and the untick
        // would silently do nothing.
        const matches =
          pick.type === "bank"
            ? (element as { bank_field_id?: string }).bank_field_id === pick.field.id
            : pick.type === "system_field"
              ? element.kind === "system_field" && element.system_field === pick.systemField
              : element.kind === pick.type;
        if (matches) return { pageIndex, path: location.path, index: location.index };
      }
    }
    return null;
  }

  insertElement(pageIndex: number, atIndex: number, kind: NewElementKind): void {
    this.insertElementAtPath(pageIndex, [], atIndex, makeNewElement(kind));
  }

  /** Insert a leaf-kind element at an arbitrary tree path. */
  insertLeafAtPath(
    pageIndex: number,
    path: ContainerPath,
    atIndex: number,
    kind: NewElementKind,
  ): void {
    this.insertElementAtPath(pageIndex, path, atIndex, makeNewElement(kind));
  }

  /** Insert a bank-linked leaf element at an arbitrary tree path. */
  removeElement(pageIndex: number, elementIndex: number): void {
    this.removeElementAtPath(pageIndex, [], elementIndex);
  }

  duplicateElementAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const src = list?.[index];
    // System fields and singleton kinds (program, request category) may never
    // be duplicated — the schema allows at most one per definition. The check
    // is subtree-deep: a field_group/columns wrapping a singleton is equally
    // un-duplicable.
    if (!src || src.kind === "system_field" || subtreeContainsSingleton(src)) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      const source = target?.[index];
      if (!target || !source || source.kind === "system_field") return;
      if (subtreeContainsSingleton(source)) return;
      const dup = clone(source) as TIntakeTreeElement;
      regenerateSubtreeIds(dup, newCloneId);
      annotateDuplicateLabels(dup);
      target.splice(index + 1, 0, dup);
    });
    if (this.draft) {
      const updatedPage = this.draft.pages[pageIndex];
      if (!updatedPage) return;
      const updated = getChildrenAtPath(updatedPage, path);
      const inserted = updated?.[index + 1];
      if (inserted && inserted.id === newCloneId) {
        this.selectedElementId = inserted.id;
      }
    }
  }

  duplicateElement(pageIndex: number, elementIndex: number): void {
    this.duplicateElementAtPath(pageIndex, [], elementIndex);
  }

  /**
   * Duplicates a bank-linked global custom field as a form-specific copy:
   * - Strips `bank_field_id` so the copy is disconnected from the global library
   *   (future changes to the global field will not affect this copy — PRD §2.3.b).
   * - Deep-clones options/validation/description; clears per-option `hidden`
   *   flags (form-specific fields don't have the hide-from-requesters concept).
   * - Appends a localized "(Copy)" suffix to labels; assigns a fresh UUID
   *   `name` so the copy is uniquely identifiable.
   * - Clears `visible_if` to avoid carrying conditional-logic refs that may not
   *   make sense once the copy is form-specific.
   * No-ops for system fields and elements without `bank_field_id`.
   */
  duplicateElementAsFormSpecific(pageIndex: number, elementIndex: number): void {
    this.duplicateElementAsFormSpecificAtPath(pageIndex, [], elementIndex);
  }

  duplicateElementAsFormSpecificAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
  ): void {
    if (!this.draft) return;
    const page = this.draft.pages[pageIndex];
    if (!page) return;
    const list = getChildrenAtPath(page, path);
    const src = list?.[index];
    if (!src || src.kind === "system_field") return;
    const bankFieldId = (src as { bank_field_id?: string }).bank_field_id;
    if (!bankFieldId) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftPage = draft.pages[pageIndex];
      if (!draftPage) return;
      const target = getChildrenAtPath(draftPage, path);
      const source = target?.[index];
      if (!target || !source || source.kind === "system_field") return;
      const dup = clone(source) as TIntakeTreeElement;
      dup.id = newCloneId;
      if (dup.kind !== "system_field" && "name" in dup) {
        // Strip the global link so the copy is purely form-specific.
        delete (dup as { bank_field_id?: string }).bank_field_id;
        // Clear conditional logic on the copy — refs to other fields may be
        // intact, but the source rule was authored against the global field's
        // identity and may surprise authors. Safer to drop and let them re-add.
        dup.visible_if = null;
        dup.name = newId();
        if (dup.label) {
          dup.label = {
            en: `${dup.label.en ?? ""} (Copy)`,
            fr: `${dup.label.fr ?? ""} (Copie)`,
          };
        }
        // Clear per-option `hidden` flags — bank-only concept (PRD §2.5.b).
        const withOptions = dup as { options?: Array<{ hidden?: boolean }> };
        if (withOptions.options) {
          for (const option of withOptions.options) {
            delete option.hidden;
          }
        }
      }
      target.splice(index + 1, 0, dup);
    });
    if (this.draft) {
      const updatedPage = this.draft.pages[pageIndex];
      if (!updatedPage) return;
      const updated = getChildrenAtPath(updatedPage, path);
      const inserted = updated?.[index + 1];
      if (inserted && inserted.id === newCloneId) {
        this.selectedElementId = inserted.id;
      }
    }
  }

  moveElement(pageIndex: number, fromIndex: number, toIndex: number): void {
    this.moveElementBetweenPaths(pageIndex, [], fromIndex, [], toIndex);
  }

  /**
   * Move an element (preserving its subtree) from a position on one page to
   * the end of another page's root container. System fields and self-page
   * moves are rejected — they are the caller's responsibility to avoid.
   *
   * Names are preserved so cross-page conditional refs continue to resolve.
   */
  moveElementToPage(
    sourcePageIndex: number,
    sourcePath: ContainerPath,
    sourceIndex: number,
    targetPageId: string,
  ): void {
    if (!this.draft) return;
    const sourcePage = this.draft.pages[sourcePageIndex];
    if (!sourcePage) return;
    const sourceList = getChildrenAtPath(sourcePage, sourcePath);
    const src = sourceList?.[sourceIndex];
    if (!src || isSystemFieldElement(src)) return;
    const targetPageIndex = this.draft.pages.findIndex((p) => p.id === targetPageId);
    if (targetPageIndex === -1) return;
    if (sourcePage.id === targetPageId && sourcePath.length === 0) {
      // Same-page root → no-op (existing reorder UI covers in-page moves).
      return;
    }
    const movedId = src.id;
    this.applyDefinitionPatch((draft) => {
      const draftSource = draft.pages[sourcePageIndex];
      if (!draftSource) return;
      const fromList = getChildrenAtPath(draftSource, sourcePath);
      if (!fromList) return;
      const [moved] = fromList.splice(sourceIndex, 1);
      if (!moved) return;
      const draftTarget = draft.pages[targetPageIndex];
      if (!draftTarget) {
        // Restore on the unlikely chance the target vanished mid-mutation.
        fromList.splice(sourceIndex, 0, moved);
        return;
      }
      draftTarget.elements.push(moved);
    });
    this.activePageId = targetPageId;
    this.selectedElementId = movedId;
  }

  /**
   * Duplicate an element (with its subtree) onto the END of another page's
   * root container. Every node in the clone gets a fresh ``id``; leaves get a
   * fresh ``name``. Container ``columns[*].id`` are regenerated too.
   * System fields cannot be duplicated to another page.
   */
  duplicateElementToPage(
    sourcePageIndex: number,
    sourcePath: ContainerPath,
    sourceIndex: number,
    targetPageId: string,
  ): void {
    if (!this.draft) return;
    const sourcePage = this.draft.pages[sourcePageIndex];
    if (!sourcePage) return;
    const sourceList = getChildrenAtPath(sourcePage, sourcePath);
    const src = sourceList?.[sourceIndex];
    // Singleton kinds (program, request category) — including containers that
    // wrap one — may exist at most once per definition, so cross-page
    // duplication is a no-op for them just like in-place duplication. Moving
    // remains allowed (still exactly one instance).
    if (!src || isSystemFieldElement(src) || subtreeContainsSingleton(src)) return;
    const targetPageIndex = this.draft.pages.findIndex((p) => p.id === targetPageId);
    if (targetPageIndex === -1) return;
    const newCloneId = newId();
    this.applyDefinitionPatch((draft) => {
      const draftSource = draft.pages[sourcePageIndex];
      if (!draftSource) return;
      const fromList = getChildrenAtPath(draftSource, sourcePath);
      const source = fromList?.[sourceIndex];
      if (!source || source.kind === "system_field") return;
      const dup = clone(source) as TIntakeTreeElement;
      regenerateSubtreeIds(dup, newCloneId);
      annotateDuplicateLabels(dup);
      const draftTarget = draft.pages[targetPageIndex];
      if (!draftTarget) return;
      draftTarget.elements.push(dup);
    });
    this.activePageId = targetPageId;
    this.selectedElementId = newCloneId;
  }

  /**
   * The id of the element at this location, for a cross-page MOVE whose destination
   * page is NOT in this draft. The Form Workflow builder's Edit Page modal scopes
   * the draft to a single page (NG-3856), so the destination lives in the workflow
   * store, not here — the move is a backend reparent keyed by element id (which
   * preserves ids, names and inbound refs), not a draft mutation like
   * {@link moveElementToPage}. Returns ``null`` for a system field (which can't be
   * moved) or a location that resolves to nothing. Pure read.
   */
  elementIdForMoveToPage(pageIndex: number, path: ContainerPath, index: number): string | null {
    if (!this.draft) return null;
    const page = this.draft.pages[pageIndex];
    if (!page) return null;
    const src = getChildrenAtPath(page, path)?.[index];
    if (!src || isSystemFieldElement(src)) return null;
    return src.id;
  }

  /**
   * Return a plain deep clone of an element (with its subtree) for a DUPLICATE
   * to a page that is NOT in this draft (NG-3856, see
   * {@link cloneElementForMoveToPage}). Every node gets a fresh id and every
   * leaf a fresh name, and labels are annotated "(copy)", mirroring
   * {@link duplicateElementToPage}. System fields and singleton kinds (program,
   * request category) — or containers wrapping one — can't be duplicated:
   * returns ``null`` for them. Pure read — the source is left untouched; the
   * caller only appends the clone to the destination page.
   */
  cloneElementForDuplicateToPage(
    pageIndex: number,
    path: ContainerPath,
    index: number,
  ): TIntakeTreeElement | null {
    if (!this.draft) return null;
    const page = this.draft.pages[pageIndex];
    if (!page) return null;
    const src = getChildrenAtPath(page, path)?.[index];
    if (!src || isSystemFieldElement(src) || subtreeContainsSingleton(src)) return null;
    const dup = clone(src);
    // Fresh ids + names, then rewrite any visible_if / formula ref that pointed
    // within the subtree to the clone's new names, so the copy's internal conditions
    // read the copied fields, not the originals (NG-3856). External refs are kept.
    const nameMap = new Map<string, string>();
    regenerateSubtreeIdsTrackingNames(dup, newId(), nameMap);
    reconcileDuplicateSubtreeRefs(dup, nameMap);
    annotateDuplicateLabels(dup);
    return dup;
  }

  setElementFieldAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: TIntakeTreeElement) => void,
  ): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (!page) return;
      const list = getChildrenAtPath(page, path);
      const el = list?.[index];
      if (el) updater(el);
    });
  }

  setElementField(
    pageIndex: number,
    elementIndex: number,
    updater: (el: TIntakeElement) => void,
  ): void {
    this.setElementFieldAtPath(pageIndex, [], elementIndex, (el) => {
      // Top-level setElementField predates containers; the existing callers
      // only operate on leaf TIntakeElement kinds. Safe cast: containers are
      // never reachable through this API.
      if (el.kind === "field_group" || el.kind === "columns") return;
      if (el.kind === "info_text" || el.kind === "rich_text" || el.kind === "divider") return;
      updater(el);
    });
  }

  /**
   * Patch a container element's properties — group title / collapsed, columns
   * shape, info_text content. Targets a container by its location in the tree.
   */
  setContainerProperty(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: TIntakeTreeElement) => void,
  ): void {
    this.setElementFieldAtPath(pageIndex, path, index, updater);
  }

  /**
   * Adjust the column count of a ``columns`` container in 2..4. When growing,
   * new empty column slots are appended; when shrinking, the trailing column
   * children are concatenated onto the previous column so no data is lost.
   */
  setColumnsCount(pageIndex: number, path: ContainerPath, index: number, nextCount: number): void {
    const target = Math.max(2, Math.min(4, nextCount));
    this.setElementFieldAtPath(pageIndex, path, index, (el) => {
      if (el.kind !== "columns") return;
      const current = el.columns.length;
      if (target === current) return;
      if (target > current) {
        for (let i = current; i < target; i += 1) {
          el.columns.push({ id: newId(), children: [] });
        }
        return;
      }
      // Shrinking — fold trailing column children into the last surviving column.
      const survivors = el.columns.slice(0, target);
      const dropped = el.columns.slice(target);
      const lastSurvivor = survivors[survivors.length - 1];
      if (lastSurvivor) {
        for (const col of dropped) lastSurvivor.children.push(...col.children);
      }
      el.columns = survivors;
    });
  }

  private mutateChoiceAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    updater: (el: ChoiceElement) => void,
  ): void {
    this.applyDefinitionPatch((draft) => {
      const page = draft.pages[pageIndex];
      if (!page) return;
      const list = getChildrenAtPath(page, path);
      const el = list?.[index];
      if (isChoiceElement(el)) updater(el);
    });
  }

  addOptionAtPath(pageIndex: number, path: ContainerPath, index: number): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      el.options.push(makeOption(el.options.length));
    });
  }

  addOption(pageIndex: number, elementIndex: number): void {
    this.addOptionAtPath(pageIndex, [], elementIndex);
  }

  removeOptionAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    optionIndex: number,
  ): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      el.options.splice(optionIndex, 1);
    });
  }

  removeOption(pageIndex: number, elementIndex: number, optionIndex: number): void {
    this.removeOptionAtPath(pageIndex, [], elementIndex, optionIndex);
  }

  moveOptionAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    fromIndex: number,
    toIndex: number,
  ): void {
    if (fromIndex === toIndex) return;
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      const [moved] = el.options.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, el.options.length));
      el.options.splice(insertAt, 0, moved);
    });
  }

  moveOption(pageIndex: number, elementIndex: number, fromIndex: number, toIndex: number): void {
    this.moveOptionAtPath(pageIndex, [], elementIndex, fromIndex, toIndex);
  }

  setOptionFieldAtPath(
    pageIndex: number,
    path: ContainerPath,
    index: number,
    optionIndex: number,
    updater: (option: TIntakeChoiceOption) => void,
  ): void {
    this.mutateChoiceAtPath(pageIndex, path, index, (el) => {
      const option = el.options[optionIndex];
      if (option) updater(option);
    });
  }

  setOptionField(
    pageIndex: number,
    elementIndex: number,
    optionIndex: number,
    updater: (option: TIntakeChoiceOption) => void,
  ): void {
    this.setOptionFieldAtPath(pageIndex, [], elementIndex, optionIndex, updater);
  }

  /** Persist the form. ``metaOnly`` omits the ``definition`` from the PATCH:
   *  a Form Workflow form's page content is owned + persisted by the workflow
   *  store (#2163), so the legacy PATCH must not send the definition (it would
   *  clobber it) — but the form META (name / teams / default request category /
   *  default request owner) still lives on this legacy PATCH. Saving meta-only
   *  rather than skipping the save entirely is what lets a workflow-created form
   *  set its required category/owner so it can publish. */
  async save(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    const run = this.performSave(slug, options);
    this.saveInFlight = run;
    try {
      await run;
    } finally {
      // Post-`await` code runs outside the action `save` opened, so both the
      // read and the write of `saveInFlight` need their own action.
      runInAction(() => {
        if (this.saveInFlight === run) this.saveInFlight = null;
      });
    }
  }

  /** Await an in-flight PATCH, then persist if the draft is still dirty.
   *  Publish calls this so a click inside the 1000ms autosave debounce
   *  cannot POST /publish/ before the meta PATCH lands. Throws on failure. */
  async flushPendingSave(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    if (this.saveInFlight) await this.saveInFlight;
    if (this.isDirty) await this.save(slug, options);
  }

  private async performSave(slug: string, options: { metaOnly?: boolean } = {}): Promise<void> {
    if (!this.form || !this.draft) return;
    const metaOnly = options.metaOnly ?? false;
    runInAction(() => {
      this.isSaving = true;
    });
    this.clearErrors();
    // Snapshot the editable state we're sending. Setters reassign ``topLevel``
    // and ``draft``, so a changed identity on return means the user kept
    // editing while the PATCH was in flight — the server echo is then stale and
    // must not clobber the newer local value (the autosave letter-drop bug).
    const topLevelAtSave = this.topLevel;
    const draftAtSave = this.draft;
    const formId = this.form.id;
    const body = {
      // The workflow store owns the definition for a workflow form; never send
      // it meta-only or it would overwrite the workflow-authored content.
      ...(metaOnly ? {} : { definition: this.draft }),
      name: this.topLevel.name,
      // Normalized on the way OUT only. Tiptap leaves `<p></p>` behind for a
      // description the user emptied, and persisting that reads as content:
      // it would render a padded empty block on the requester's form and show
      // as a pending change after a bare focus/blur. The raw editor buffer
      // stays in `topLevel` because `normalizeRichText` must never be fed back
      // to the editor as its `value` (NG-265) — the builder does exactly that.
      description_html: normalizeRichText(this.topLevel.descriptionHtml),
      // No ``is_active``: it is a live switch that ``toggleFormActive`` writes on
      // its own, not editorial draft content. Echoing it from this snapshot let
      // a debounced autosave revert an activation made after the builder loaded
      // (a toggle while dirty, another tab, an API publish), leaving the form
      // inactive so its published workflow 404'd for requesters (NG-1211).
      teams: this.topLevel.teams,
      request_type: this.topLevel.request_type,
      default_request_category_value_id: this.topLevel.default_request_category_value_id,
      default_request_owner_ids: this.topLevel.default_request_owner_ids,
      managing_team_id: this.topLevel.managingTeamId,
    };
    // What this save changed for `form_updated` (NG-2058): the body carries every
    // field on every save, so compare it with the stored form, read the way
    // `hydrateFromForm` reads it so a default the builder filled in doesn't count.
    const fields = changedFields(body, savedFormBody(this.form));
    try {
      const next = await this.rootStore.intakeService.updateForm(slug, formId, body);
      this.markFormsStale(slug);
      runInAction(() => {
        this.adoptFormEcho(slug, next);
        const editedDuringSave = this.topLevel !== topLevelAtSave || this.draft !== draftAtSave;
        if (editedDuringSave) {
          // Keep the user's in-progress edits; refresh only server-owned meta.
          // ``_dirty`` stays true so the next debounced save persists them.
          this.applyServerMeta(next);
        } else if (metaOnly) {
          // Meta-only never sent the draft, so re-hydrating from the echo would
          // needlessly reset the builder view. Refresh server-owned meta and
          // clear dirty — this save captured everything, so the autosave loop
          // must not re-fire (``applyServerMeta`` alone leaves ``_dirty`` set).
          this.applyServerMeta(next);
          this._dirty = false;
        } else {
          this.hydrateFromForm(next);
        }
      });
      if (fields.length > 0) track("form_updated", { form_id: formId, fields, source: "page" });
    } catch (err) {
      // Pass the in-memory draft so the parser can resolve nested element
      // paths (``children``/``columns`` segments) back to a stable id. The
      // inline-error filter then targets the right row regardless of depth.
      const { inline, banner } = parseSaveErrors(err, this.draft);
      runInAction(() => {
        this.inlineErrors = inline;
        this.bannerErrors = banner;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** Push the current draft live. Backend gates (must-be-active +
   *  required-fields-blank + strict definition schema) are also enforced
   *  by ``publishDisabledReason`` so the FE rarely fires this against a
   *  state that will 409/422. */
  async publishForm(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    if (!this.form) return;
    runInAction(() => {
      this.isPublishing = true;
    });
    this.clearErrors();
    try {
      const next = await this.rootStore.intakeService.publishForm(slug, this.form.id);
      runInAction(() => {
        this.adoptFormEcho(slug, next);
        this.hydrateFromForm(next);
      });
      track("form_published", { form_id: next.id, source });
      this.markFormsStale(slug);
    } catch (err) {
      runInAction(() => {
        // `this.draft` is read inside the action too: this runs after an
        // `await`, i.e. outside any reaction, where a bare read trips mobx's
        // `observableRequiresReaction` guard.
        const { inline, banner } = parseSaveErrors(err, this.draft);
        this.inlineErrors = inline;
        this.bannerErrors = banner;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isPublishing = false;
      });
    }
  }

  /** Drop the unpublished draft and re-hydrate from the last published
   *  snapshot. The form list / builder visibly snap back to live. */
  async discardDraft(slug: string, source: AnalyticsSource = "page"): Promise<void> {
    if (!this.form) return;
    runInAction(() => {
      this.isSaving = true;
    });
    try {
      const next = await this.rootStore.intakeService.discardFormDraft(slug, this.form.id);
      runInAction(() => {
        this.adoptFormEcho(slug, next);
        this.hydrateFromForm(next);
        this._dirty = false;
      });
      track("form_draft_discarded", { form_id: next.id, source });
      this.markFormsStale(slug);
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async toggleFormActive(
    slug: string,
    formId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const target = this.builderForm?.id === formId ? this.builderForm : undefined;
    if (!target) return;
    const previous = target.is_active;
    const next = !previous;
    runInAction(() => {
      if (this.builderForm?.id === formId) {
        this.builderForm = { ...this.builderForm, is_active: next };
      }
      // Unconditional: the autosave never sends ``is_active``, so a dirty draft
      // cannot overwrite it and the switch must show what was just written.
      if (this.activeFormId === formId && this.form) {
        this.topLevel.is_active = next;
      }
    });
    try {
      const updated = await this.rootStore.intakeService.updateForm(slug, formId, {
        is_active: next,
      });
      runInAction(() => {
        this.adoptFormEcho(slug, updated);
      });
      track(next ? "form_activated" : "form_deactivated", { form_id: formId, source });
      this.markFormsStale(slug);
    } catch (err) {
      runInAction(() => {
        if (this.builderForm?.id === formId) {
          this.builderForm = { ...this.builderForm, is_active: previous };
        }
        if (this.activeFormId === formId) {
          this.topLevel.is_active = previous;
        }
      });
      throw err;
    }
  }

  async deleteForm(slug: string, formId: string, source: AnalyticsSource = "page"): Promise<void> {
    await this.rootStore.intakeService.deleteForm(slug, formId);
    track("form_deleted", { form_id: formId, source });
    this.markFormsStale(slug);
    const client = peekQueryClient();
    if (client) removeCachedBuilderForm(client, slug, formId);
    runInAction(() => {
      if (this.activeFormId === formId) {
        this.activeFormId = null;
        this.builderForm = null;
        this.draft = null;
        this.topLevel = emptyTopLevel();
      }
    });
  }

  /**
   * Open the builder on `form`, a cached list row while the workflow's detail
   * read is in flight (`settled` false), then that read's answer. The draft is
   * seeded once per open (NG-3051): the row seeds it so the page draws at once,
   * read-only while `can_edit` is unknown; the settled answer seeds it once
   * more, and nothing seeds it after that until the builder closes. A dirty
   * draft is never seeded over.
   */
  openBuilderForm(form: TIntakeForm, settled: boolean): void {
    const sameOpen = this.activeFormId === form.id && this.builderSeed !== null;
    if (sameOpen && (this.builderSeed === "detail" || this._dirty)) return;
    this.hydrateFromForm(form);
    this.builderSeed = settled ? "detail" : "row";
  }

  /** The builder closed on `formId`: its next open seeds the draft again. */
  closeBuilderForm(formId: string): void {
    if (this.activeFormId === formId) this.builderSeed = null;
  }

  /** A form write's echo: the builder's cached read and, for the open
   *  workflow, the draft's baseline (NG-2808). */
  private adoptFormEcho(slug: string, next: TIntakeForm): void {
    const client = peekQueryClient();
    if (client) putCachedBuilderForm(client, slug, next);
    if (this.builderForm?.id === next.id) this.builderForm = next;
  }

  /** A form the forms table wrote (the active switch): the builder's cached
   *  read of it, so the builder does not open on the old value. */
  applyFormUpdate(slug: string, updated: TIntakeForm): void {
    this.adoptFormEcho(slug, updated);
  }

  /** A form the forms table deleted: drop the builder's cached read of it. */
  removeForm(slug: string, formId: string): void {
    const client = peekQueryClient();
    if (client) removeCachedBuilderForm(client, slug, formId);
  }

  setSubmissionsFormFilter(value: readonly string[]): void {
    this.submissionsFormFilter = [...value];
  }

  setSubmissionsProgramFilter(value: readonly string[]): void {
    this.submissionsProgramFilter = [...value];
  }

  setSubmissionsHomeTeamFilter(value: readonly string[]): void {
    this.submissionsHomeTeamFilter = [...value];
  }

  setSubmissionsHomeTeamEnabled(enabled: boolean): void {
    this.submissionsHomeTeamEnabled = enabled;
  }

  setSubmissionsSearch(value: string): void {
    this.submissionsSearch = value;
  }

  setSubmissionsStatusFilter(value: readonly string[]): void {
    this.submissionsStatusFilter = [...value];
  }

  setSubmissionsRequestedByFilter(value: readonly string[]): void {
    this.submissionsRequestedByFilter = [...value];
  }

  setSubmissionsOwnerFilter(value: readonly string[]): void {
    this.submissionsOwnerFilter = [...value];
  }

  setSubmissionsCategoryFilter(value: readonly string[]): void {
    this.submissionsCategoryFilter = [...value];
  }

  setSubmissionsSlaFilter(value: readonly string[]): void {
    this.submissionsSlaFilter = [...value];
  }

  setSubmissionsPriorityFilter(value: readonly string[]): void {
    this.submissionsPriorityFilter = [...value];
  }

  setSubmissionsCreatedAfter(value: string): void {
    this.submissionsCreatedAfter = value;
  }

  setSubmissionsCreatedBefore(value: string): void {
    this.submissionsCreatedBefore = value;
  }

  setSubmissionsUpdatedAfter(value: string): void {
    this.submissionsUpdatedAfter = value;
  }

  setSubmissionsUpdatedBefore(value: string): void {
    this.submissionsUpdatedBefore = value;
  }

  /** Reset every column-header filter (and the search box) to its empty
   *  state, so a mount / workspace switch starts on the full unfiltered list.
   *  Called by `hydrateSubmissionsPrefs` as the base it restores onto — never
   *  from the list effect directly, which would clear OUTSIDE the write-back
   *  guard and let the persistence reaction wipe the key being restored. */
  clearSubmissionsFilters(): void {
    this.submissionsFormFilter = [];
    this.submissionsProgramFilter = [];
    this.submissionsHomeTeamFilter = [];
    this.submissionsSearch = "";
    this.submissionsStatusFilter = [];
    this.submissionsRequestedByFilter = [];
    this.submissionsOwnerFilter = [];
    this.submissionsCategoryFilter = [];
    this.submissionsSlaFilter = [];
    this.submissionsPriorityFilter = [];
    this.submissionsCreatedAfter = "";
    this.submissionsCreatedBefore = "";
    this.submissionsUpdatedAfter = "";
    this.submissionsUpdatedBefore = "";
    this.submissionsGroupBy = "none";
    // Reset with the rest: a view that does not narrow the archive must not
    // inherit "archived only" from whatever the user was last looking at.
    this.submissionsArchivedView = "active";
  }

  /**
   * The current submissions filter set, in the shape a saved view stores.
   *
   * The read counterpart of `hydrateSubmissionsFilters`; both live here so the
   * field list stays in one file rather than one copy per consumer.
   */
  get submissionsFilterState(): RequestViewFilterState {
    return {
      search: this.submissionsSearch,
      formIds: [...this.submissionsFormFilter],
      programIds: [...this.submissionsProgramFilter],
      homeTeamIds: [...this.submissionsHomeTeamFilter],
      statusIds: [...this.submissionsStatusFilter],
      requestedByIds: [...this.submissionsRequestedByFilter],
      ownerIds: [...this.submissionsOwnerFilter],
      // The Requests list no longer filters by team (NG-3214). The shared saved-view
      // shape still carries teamIds for other consumers, so emit an empty set rather
      // than resurrecting a filter the list cannot show or clear.
      teamIds: [],
      categoryIds: [...this.submissionsCategoryFilter],
      slaStates: [...this.submissionsSlaFilter],
      priorities: [...this.submissionsPriorityFilter],
      createdAfter: this.submissionsCreatedAfter,
      createdBefore: this.submissionsCreatedBefore,
      updatedAfter: this.submissionsUpdatedAfter,
      updatedBefore: this.submissionsUpdatedBefore,
      archivedView: this.submissionsArchivedView,
      groupBy: this.submissionsGroupBy,
    };
  }

  /**
   * REPLACE the whole submissions filter set with a saved view's state.
   *
   * A full replace, not a merge, and that is the point: this store is a singleton
   * shared by the plain Requests list and every request-view page, so a merge would
   * let one surface's filters survive a navigation into the other. Clearing first
   * means a mount is always the only thing deciding what is filtered (NG-528).
   *
   * INVARIANT: keep this covering every field `clearSubmissionsFilters` resets —
   * a field cleared here but not assigned silently drops that filter when a view
   * opens, and one assigned but not cleared leaks into the next surface.
   *
   * Dropping `submissionsPrefsSlug` is what keeps a view's filters out of the
   * plain list's persisted prefs (NG-517 review). The persistence reactions
   * flush when the OUTERMOST action ends and read the slug then — so nulling it
   * here means this seed, and every filter/sort change made while the view owns
   * the store, has no key to write under. The next plain-list mount re-arms
   * persistence via `hydrateSubmissionsPrefs`, which also re-reads the stored
   * set this method just displaced.
   */
  hydrateSubmissionsFilters(state: RequestViewFilterState): void {
    this.submissionsPrefsSlug = null;
    this.clearSubmissionsFilters();
    this.submissionsSearch = state.search;
    this.submissionsFormFilter = [...state.formIds];
    // ``?? []``: a view saved before NG-1631 has no ``programIds`` key.
    this.submissionsProgramFilter = [...(state.programIds ?? [])];
    // ``?? []``: a view saved before NG-3462 has no ``homeTeamIds`` key.
    this.submissionsHomeTeamFilter = [...(state.homeTeamIds ?? [])];
    this.submissionsStatusFilter = [...state.statusIds];
    this.submissionsRequestedByFilter = [...state.requestedByIds];
    this.submissionsOwnerFilter = [...state.ownerIds];
    // state.teamIds is intentionally ignored: the Requests list dropped team
    // filtering (NG-3214), so a view saved with a team no longer re-applies it.
    this.submissionsCategoryFilter = [...state.categoryIds];
    this.submissionsSlaFilter = [...state.slaStates];
    this.submissionsPriorityFilter = [...state.priorities];
    this.submissionsCreatedAfter = state.createdAfter;
    this.submissionsCreatedBefore = state.createdBefore;
    this.submissionsUpdatedAfter = state.updatedAfter;
    this.submissionsUpdatedBefore = state.updatedBefore;
    this.submissionsArchivedView = state.archivedView;
    this.submissionsGroupBy = state.groupBy;
  }

  // ---- DataTable bridge ----

  /** Bridges the flat submissions filter fields to the TanStack
   *  ColumnFiltersState the shared DataTable consumes. Column ids match the
   *  ColumnDef ids in intake-submissions-columns.tsx. */
  get submissionsColumnFilters(): ColumnFiltersState {
    // Search is deliberately NOT a columnFilters entry — it stays a top-level
    // toolbar input (spec §14, parity with the legacy table). Only column-header
    // filters live here.
    const out: ColumnFiltersState = [];
    if (this.submissionsStatusFilter.length > 0) {
      out.push({
        id: "status",
        value: { type: "multi", value: [...this.submissionsStatusFilter] },
      });
    }
    if (this.submissionsRequestedByFilter.length > 0) {
      out.push({
        id: "requestedBy",
        value: { type: "multi", value: [...this.submissionsRequestedByFilter] },
      });
    }
    if (this.submissionsOwnerFilter.length > 0) {
      out.push({ id: "owner", value: { type: "multi", value: [...this.submissionsOwnerFilter] } });
    }
    if (this.submissionsFormFilter.length > 0) {
      out.push({
        id: "formUsed",
        value: { type: "multi", value: [...this.submissionsFormFilter] },
      });
    }
    if (this.submissionsProgramFilter.length > 0) {
      out.push({
        id: "programs",
        value: { type: "multi", value: [...this.submissionsProgramFilter] },
      });
    }
    if (this.submissionsHomeTeamEnabled && this.submissionsHomeTeamFilter.length > 0) {
      out.push({
        id: "homeTeam",
        value: { type: "multi", value: [...this.submissionsHomeTeamFilter] },
      });
    }
    if (this.submissionsCategoryFilter.length > 0) {
      out.push({
        id: "requestCategory",
        value: { type: "multi", value: [...this.submissionsCategoryFilter] },
      });
    }
    if (this.submissionsSlaFilter.length > 0) {
      out.push({ id: "sla", value: { type: "multi", value: [...this.submissionsSlaFilter] } });
    }
    if (this.submissionsPriorityFilter.length > 0) {
      out.push({
        id: "priority",
        value: { type: "multi", value: [...this.submissionsPriorityFilter] },
      });
    }
    if (this.submissionsCreatedAfter || this.submissionsCreatedBefore) {
      out.push({
        id: "created",
        value: {
          type: "dateRange",
          value: { from: this.submissionsCreatedAfter, to: this.submissionsCreatedBefore },
        },
      });
    }
    if (this.submissionsUpdatedAfter || this.submissionsUpdatedBefore) {
      out.push({
        id: "lastUpdated",
        value: {
          type: "dateRange",
          value: { from: this.submissionsUpdatedAfter, to: this.submissionsUpdatedBefore },
        },
      });
    }
    return out;
  }

  /** Fans a TanStack ColumnFiltersState back into the flat filter fields
   *  (facade); the new set is a new list key, so the table reads its page 1.
   *  Filters absent from ``next`` are cleared. */
  setSubmissionsColumnFilters(next: ColumnFiltersState): void {
    // Persistence is handled by `watchSubmissionsListPrefs`, not here — see why
    // there.
    this.applySubmissionsColumnFilters(next);
  }

  /** Apply a filter set to the flat fields. No fetch and no persistence of its
   *  own, so hydration can reuse it without writing back what it just read. */
  private applySubmissionsColumnFilters(next: ColumnFiltersState): void {
    // Search is owned by the toolbar input, not the column-header filters, so
    // it is intentionally left untouched here (see submissionsColumnFilters).
    const find = (id: string) =>
      next.find((f) => f.id === id)?.value as
        | { type: "multi"; value: string[] }
        | { type: "dateRange"; value: { from: string; to: string } }
        | undefined;

    this.submissionsStatusFilter = (find("status") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsRequestedByFilter =
      (find("requestedBy") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsOwnerFilter = (find("owner") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsFormFilter = (find("formUsed") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsProgramFilter =
      (find("programs") as { value: string[] } | undefined)?.value ?? [];
    // With the column off its filter is not in `next`; keep the remembered one
    // rather than wiping it, so turning the switch back on restores it.
    if (this.submissionsHomeTeamEnabled) {
      this.submissionsHomeTeamFilter =
        (find("homeTeam") as { value: string[] } | undefined)?.value ?? [];
    }
    this.submissionsCategoryFilter =
      (find("requestCategory") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsSlaFilter = (find("sla") as { value: string[] } | undefined)?.value ?? [];
    this.submissionsPriorityFilter =
      (find("priority") as { value: string[] } | undefined)?.value ?? [];
    const created = find("created") as { value: { from: string; to: string } } | undefined;
    this.submissionsCreatedAfter = created?.value.from ?? "";
    this.submissionsCreatedBefore = created?.value.to ?? "";
    const updated = find("lastUpdated") as { value: { from: string; to: string } } | undefined;
    this.submissionsUpdatedAfter = updated?.value.from ?? "";
    this.submissionsUpdatedBefore = updated?.value.to ?? "";
  }

  /** Stores the TanStack sort and resolves the whitelisted server token.
   *  Non-sortable / unknown column ids clear the token. */
  setSubmissionsSorting(next: SortingState): void {
    // Persistence is handled by `watchSubmissionsListPrefs`, not here.
    this.applySubmissionsSorting(next);
  }

  /** Sort state + resolved server token, without the persistence write — so
   *  hydration can restore a stored sort into the list's first key. */
  private applySubmissionsSorting(next: SortingState): void {
    this.submissionsSorting = next;
    const first = next[0];
    const token = first ? SUBMISSIONS_COLUMN_TO_ORDER_BY[first.id] : undefined;
    this.submissionsOrderBy = token ? `${first.desc ? "-" : ""}${token}` : "";
  }

  setSubmissionsRowSelection(next: RowSelectionState): void {
    this.submissionsRowSelection = next;
  }

  /** Switch the table's server-side grouping dimension: a new list key, whose
   *  page 1 is re-ordered so the chosen group's rows are contiguous. */
  setSubmissionsGroupBy(next: SubmissionGroupBy): void {
    this.submissionsGroupBy = next;
  }

  /** Group-by override for the table list fetch only. Returns ``undefined``
   *  when ungrouped so the param is omitted, and so the board/export callers
   *  (which don't pass it) never group. */
  private groupByOverride(): Partial<TIntakeSubmissionsListQuery> | undefined {
    return this.submissionsGroupBy === "none" ? undefined : { group_by: this.submissionsGroupBy };
  }

  private buildSubmissionsListQuery(
    cursor: string | null,
    overrides?: Partial<TIntakeSubmissionsListQuery>,
  ): TIntakeSubmissionsListQuery {
    const query: TIntakeSubmissionsListQuery = {};
    if (this.submissionsFormFilter.length > 0) query.form = [...this.submissionsFormFilter];
    if (this.submissionsProgramFilter.length > 0) {
      query.program = [...this.submissionsProgramFilter];
    }
    if (this.submissionsHomeTeamEnabled && this.submissionsHomeTeamFilter.length > 0) {
      query.home_team_ids = [...this.submissionsHomeTeamFilter];
    }
    const q = this.submissionsSearch.trim();
    if (q) query.q = q;
    if (this.submissionsStatusFilter.length > 0) query.status = [...this.submissionsStatusFilter];
    if (this.submissionsRequestedByFilter.length > 0)
      query.requested_by = [...this.submissionsRequestedByFilter];
    if (this.submissionsOwnerFilter.length > 0) query.owner = [...this.submissionsOwnerFilter];
    if (this.submissionsCategoryFilter.length > 0)
      query.category = [...this.submissionsCategoryFilter];
    if (this.submissionsSlaFilter.length > 0) query.sla_state = [...this.submissionsSlaFilter];
    if (this.submissionsPriorityFilter.length > 0)
      query.priority_tier = [...this.submissionsPriorityFilter];
    if (this.submissionsCreatedAfter) query.submitted_after = this.submissionsCreatedAfter;
    if (this.submissionsCreatedBefore) query.submitted_before = this.submissionsCreatedBefore;
    if (this.submissionsUpdatedAfter) query.updated_after = this.submissionsUpdatedAfter;
    if (this.submissionsUpdatedBefore) query.updated_before = this.submissionsUpdatedBefore;
    // "active" is the server default; only send the param when narrowing away
    // from it to keep query strings (and cache keys) minimal.
    if (this.submissionsArchivedView !== "active") query.archived = this.submissionsArchivedView;
    if (this.submissionsOrderBy) query.order_by = this.submissionsOrderBy;
    if (cursor) query.cursor = cursor;
    // Board columns override ``state`` (pin to one status) and ``per_page`` (the
    // per-column page size) on top of the shared list/search/archive filters.
    return { ...query, ...overrides };
  }

  /** :meth:`buildSubmissionsListQuery` for the table and board reads, which render
   *  the form's name and nothing else from ``form_detail``: ``lean`` drops each row's
   *  form definition, tens of KB a row, from the response (NG-2424). The export keeps
   *  the plain builder, since its endpoint takes no such param. */
  private leanListQuery(
    cursor: string | null,
    overrides?: Partial<TIntakeSubmissionsListQuery>,
  ): TIntakeSubmissionsListQuery {
    return { ...this.buildSubmissionsListQuery(cursor, overrides), lean: true };
  }

  /**
   * The requests table's parameters: its query key and its request (NG-2808).
   * The filters, sort, archive view and group-by, with no cursor.
   */
  get submissionsTableParams(): RequestsListParams {
    return this.leanListQuery(null, this.groupByOverride());
  }

  /** The parameters every board column shares: the table's, minus the status
   *  filter (each column pins its own status) and the grouping (the board has
   *  none). */
  get submissionsBoardParams(): RequestsListParams {
    const params = this.leanListQuery(null);
    delete params.status;
    return params;
  }

  /**
   * Export the submissions list as CSV. Scoped to the current filters/sort;
   * ``ids`` (selected rows) restrict the export when present. Returns the raw
   * Response so the caller can stream the file (200) or poll the token (202).
   */
  exportSubmissionsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.intakeService.exportSubmissions(
      slug,
      body,
      this.buildSubmissionsListQuery(null),
    );
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.intakeService.getExportStatus(slug, tok),
    );
  }

  /** The requests cache's forms reads, after a form write (NG-2808). */
  private markFormsStale(slug: string): void {
    const client = peekQueryClient();
    if (client) markCachedFormsStale(client, slug);
  }

  /** The stage a page submit or a step completion moved the run into, for
   *  every reader of the requests cache (NG-2808). */
  private writeCachedStage(slug: string, submissionId: string, stage: TWorkflowCurrentStage): void {
    const client = peekQueryClient();
    const scope = client ? scopeIfReady(slug, "workflow stage") : null;
    if (client && scope) putCachedStage(client, scope, submissionId, stage);
  }

  /** Remove a resolved request from the reviewer queue so a decided / returned
   *  request drops out of "My reviews" without a full refetch (the endpoint no
   *  longer returns it either — this just keeps the view immediate). */
  private dropPendingReview(slug: string, submissionId: string): void {
    // From the cached queue the queue page reads; the run has left the Review,
    // so its cached stage is re-read wherever it is shown, and so is My Work,
    // whose row shows the request's status (NG-3586).
    const client = peekQueryClient();
    if (!client) return;
    dropCachedPendingReview(client, slug, submissionId);
    invalidateCachedStage(client, slug, submissionId);
    myWorkChanged(client, slug);
  }

  /**
   * Submit the Page a request is waiting on and return the stage it moves into
   * (NG-368), and put it in the cached stage. Throws on failure; the caller
   * owns UI state.
   */
  async submitWorkflowPage(
    slug: string,
    submissionId: string,
    fieldValues: Record<string, unknown>,
    source = "page",
  ): Promise<TWorkflowCurrentStage> {
    const stage = await this.rootStore.intakeService.submitWorkflowPage(
      slug,
      submissionId,
      fieldValues,
    );
    this.writeCachedStage(slug, submissionId, stage);
    track("intake_workflow_page_submitted", { request_id: submissionId, source });
    return stage;
  }

  /**
   * Explicitly complete the Page a request is waiting on and return the stage
   * it moves into (NG-703). No field values travel with it — since NG-2537 the
   * backend checks the page's visible required fields against the run's saved
   * values instead, rejecting a blank one with a 400. Throws on failure; the
   * caller owns UI state, mirroring {@link submitWorkflowPage}.
   */
  async completeWorkflowStep(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<TWorkflowCurrentStage> {
    const stage = await this.rootStore.intakeService.completeWorkflowStep(slug, submissionId);
    this.writeCachedStage(slug, submissionId, stage);
    track("intake_workflow_step_completed", { request_id: submissionId, source });
    return stage;
  }

  /**
   * Record a reviewer's decision on a request's current Review (BE #2170) and
   * drop it from the reviewer queue. Rethrows (e.g. a 409 first-responder race)
   * so the caller can surface it.
   */
  async submitWorkflowDecision(
    slug: string,
    submissionId: string,
    decisionOptionId: string,
    reason?: TStatusReasonSelection,
    source = "page",
  ): Promise<TWorkflowDecisionResult> {
    // A row write like a status change: a list page read while the decision is
    // in flight cannot put the undecided row back (NG-2463).
    const result = await this.writeRow(slug, submissionId, () =>
      this.rootStore.intakeService.submitWorkflowDecision(
        slug,
        submissionId,
        decisionOptionId,
        reason,
      ),
    );
    this.dropPendingReview(slug, submissionId);
    // The decided request goes into every cached copy of it, so the row updates
    // wherever it is displayed (the list's review pill, NG-494) rather than each
    // call site wiring its own refetch; a decision that moves the status moves
    // the board card too.
    if (result.request) {
      this.putCachedRow(slug, result.request, result.request.status ?? undefined);
    }
    track("intake_workflow_decision_submitted", { request_id: submissionId, source });
    return result;
  }

  /**
   * Send a request paused on a Review back to its previous step (BE #2254) and
   * drop it from the reviewer queue. Rethrows so the caller can surface a race.
   *
   * **No UI calls this today, on purpose.** The "Return to previous step" button
   * was removed from the reviewer decision dialog in NG-494, and no other
   * reviewer surface offers the action — the request-detail inline panel only
   * ever had a decision select. Kept because the endpoint is live and the
   * capability is real (it reverts the run's position AND its status, records a
   * ``returned`` transition and notifies the owner — a round trip, not a
   * decline), so re-adding a trigger is a UI change rather than a rebuild.
   */
  async returnWorkflowReview(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<TWorkflowReturnResult> {
    const result = await this.rootStore.intakeService.returnWorkflowToPreviousStep(
      slug,
      submissionId,
    );
    this.dropPendingReview(slug, submissionId);
    track("intake_workflow_review_returned", { request_id: submissionId, source });
    return result;
  }

  /**
   * Read one request again after something outside a row write changed it (an
   * approval vote, a review decision made in place) and put it in every cached
   * copy: its detail and each list row and board card. `null` when the read
   * fails, which is logged.
   */
  async rereadRequest(slug: string, submissionId: string): Promise<TIntakeSubmission | null> {
    try {
      const submission = await this.rootStore.intakeService.getSubmission(slug, submissionId);
      this.writeFetchedRequest(slug, submission);
      return submission;
    } catch (err) {
      const { status } = requestErrorSummary(err);
      // A request the caller can't see answers 404 (or 403): expected, and the
      // page says No access. Anything else broke the read.
      if (status === 403 || status === 404) {
        console.warn("[intake] rereadRequest: no access", { slug, submissionId, err });
      } else {
        console.error("[intake] rereadRequest failed", { slug, submissionId, err });
      }
      return null;
    }
  }

  /**
   * Soft-delete a submission via the BE. Removes the row from every cached
   * list on success so the requests list reflects the removal without a fetch
   * round-trip. Errors propagate to the caller so
   * the UI can show a toast and keep the user on the detail page.
   */
  async deleteSubmission(slug: string, submissionId: string, source = "page"): Promise<void> {
    await this.removeSubmission(slug, submissionId);
    track("request_deleted", { request_id: submissionId, source });
  }

  /**
   * Delete several requests, one call each, as a single user action: one
   * ``request_deleted`` event carrying how many went. Stops at the first
   * failure and rethrows it, after counting the ones already deleted.
   */
  async deleteSubmissions(slug: string, submissionIds: string[], source = "bulk"): Promise<void> {
    let deleted = 0;
    try {
      for (const id of submissionIds) {
        await this.removeSubmission(slug, id);
        deleted += 1;
      }
    } finally {
      if (deleted > 0) track("request_deleted", { count: deleted, source });
    }
  }

  private async removeSubmission(slug: string, submissionId: string): Promise<void> {
    // A row write like any other (NG-3246): a list read the server answered
    // before the delete committed, landing after the row left the cache, would
    // put it back.
    await this.writeRow(slug, submissionId, async () => {
      await this.rootStore.intakeService.deleteSubmission(slug, submissionId);
      const client = peekQueryClient();
      if (client) removeCachedRequest(client, slug, submissionId);
    });
  }

  /** Switches the list's archive partition: a new list key. */
  setSubmissionsArchivedView(view: TIntakeSubmissionsArchivedView): void {
    this.submissionsArchivedView = view;
  }

  /**
   * Archive or unarchive a request and patch the result into the caches.
   *
   * Puts the server's row in every cached copy, in place: the open details
   * page reflects the new ``archived_at`` and its menu flips to Unarchive. A
   * list the row no longer belongs to (its ``?archived=`` partition) drops it
   * on its next read, which the row write marks due.
   */
  async setSubmissionArchived(
    slug: string,
    submissionId: string,
    archived: boolean,
    source = "page",
  ): Promise<void> {
    await this.writeRow(slug, submissionId, async () => {
      const row = await this.rootStore.intakeService.setSubmissionArchived(
        slug,
        submissionId,
        archived,
      );
      this.putCachedRow(slug, row);
    });
    track(archived ? "request_archived" : "request_restored", { request_id: submissionId, source });
  }

  /**
   * Optimistically merge ``partial`` into the cached submission's
   * ``response_data``, fire the PATCH, and revert on failure.
   *
   * The per-key snapshot guards against a fast typist firing a second patch
   * before the first resolves: each call snapshots the pre-call
   * ``response_data`` so a failed PATCH only reverts the keys it touched,
   * not any work the user committed in the interim. The latest server-side
   * snapshot wins on success (the BE may have mirrored typed columns or
   * sanitised the payload).
   */
  async patchSubmissionResponseData(
    slug: string,
    submissionId: string,
    partial: Record<string, unknown>,
    source = "page",
  ): Promise<void> {
    const current = this.knownRequest(slug, submissionId);
    if (!current) return;
    const prevByKey: Record<string, unknown> = {};
    for (const key of Object.keys(partial)) prevByKey[key] = current.response_data[key];
    // A per-key patch and a per-key revert, so a failed PATCH undoes only the
    // keys it touched.
    const patchCachedAnswers = (answers: Record<string, unknown>) => {
      const client = peekQueryClient();
      const row = client && findCachedRequest(client, slug, submissionId);
      if (row) {
        this.putCachedRow(slug, { ...row, response_data: { ...row.response_data, ...answers } });
      }
    };

    await this.writeRow(
      slug,
      submissionId,
      async () => {
        const updated = await this.rootStore.intakeService.patchSubmissionResponseData(
          slug,
          submissionId,
          partial,
        );
        this.putCachedRow(slug, updated);
      },
      {
        optimistic: () => patchCachedAnswers(partial),
        rollback: () => patchCachedAnswers(prevByKey),
      },
    );
    // Keys are form element names (generated ids, or a system field's kind such
    // as ``request_name``), never labels or values.
    track("request_updated", { request_id: submissionId, fields: Object.keys(partial), source });
  }

  /** A request read in full lands in the requests cache too (NG-2808): its
   *  detail entry and every list row, so a refresh after a review decision or
   *  an approval reaches the readers on Query. A full read may seed the detail. */
  private writeFetchedRequest(slug: string, submission: TIntakeSubmission): void {
    const client = peekQueryClient();
    if (!client) return;
    const scope = scopeIfReady(slug, "request detail");
    if (scope) client.setQueryData(requestDetailKey(scope, submission.id), submission);
    putCachedRequest(client, slug, submission);
  }

  /** The request as the requests cache holds it. */
  private knownRequest(slug: string, submissionId: string): TIntakeSubmission | undefined {
    const client = peekQueryClient();
    return client ? findCachedRequest(client, slug, submissionId) : undefined;
  }

  /** Put ``row`` in the requests cache wherever the request is held. */
  private putCachedRow(slug: string, row: TIntakeSubmission, toStateId?: string): void {
    const client = peekQueryClient();
    if (client) putCachedRequest(client, slug, row, { toStateId });
  }

  /** A row write against the requests cache (``writeRequestRow``), so a cached
   *  page read before the write commits cannot put the old row back (NG-2463).
   *  The server logs the write in the request's history, so the activity feed
   *  is read again where it is shown, as a work item's is (NG-3587). */
  private async writeRow<T>(
    slug: string,
    submissionId: string,
    write: () => Promise<T>,
    options?: { optimistic?: () => void; rollback?: () => void },
  ): Promise<T> {
    const written = await writeRequestRow(peekQueryClient(), slug, submissionId, write, options);
    const client = peekQueryClient();
    if (client) markCachedActivityStale(client, slug, submissionId);
    return written;
  }

  async updateSubmissionStatus(
    slug: string,
    submissionId: string,
    stateId: string,
    stateDetail: { id: string; name: string; color: string; group: string },
    reason?: TStatusReasonSelection,
    source = "page",
  ): Promise<void> {
    const before = this.knownRequest(slug, submissionId);
    const from = { id: before?.status ?? null, group: before?.status_detail?.group ?? null };
    const moveCachedRow = () => {
      const client = peekQueryClient();
      const row = client && findCachedRequest(client, slug, submissionId);
      if (row)
        this.putCachedRow(slug, { ...row, status: stateId, status_detail: stateDetail }, stateId);
    };
    await this.writeRow(
      slug,
      submissionId,
      async () => {
        const updated = await this.rootStore.intakeService.updateSubmissionStatus(
          slug,
          submissionId,
          stateId,
          reason,
        );
        this.putCachedRow(slug, updated);
      },
      { optimistic: moveCachedRow },
    );
    // Status ids are the workspace's own; the groups are the fixed enum they
    // roll up to, which is what compares across workspaces.
    track("request_status_changed", {
      request_id: submissionId,
      from: from.id,
      to: stateId,
      from_group: from.group,
      to_group: stateDetail.group,
      has_reason: reason !== undefined,
      source,
    });
  }

  /**
   * Move a submission to ``newStateId`` in the board after the server has
   * already changed its state out-of-band — specifically when an approval vote
   * tips a transition over its threshold and the backend executes it in the same
   * request (see the approve/reject endpoints' ``transition_executed`` flag).
   *
   * Unlike ``updateSubmissionStatus`` this does NOT issue a PATCH (the state has
   * already moved); it fetches the fresh submission to get the correct
   * ``state_detail`` for the card pill, then moves the card across the cached
   * board columns. Without this, an approved transition only surfaces after a
   * refresh.
   */
  async relocateSubmissionToState(
    slug: string,
    submissionId: string,
    newStateId: string,
  ): Promise<void> {
    const submission = await this.rereadRequest(slug, submissionId);
    if (!submission) return;
    this.putCachedRow(slug, submission, newStateId);
  }

  /** Assign the request owners on a submission (optimistic, rolls back on error). */
  async updateSubmissionOwners(
    slug: string,
    submissionId: string,
    ownerIds: string[],
    source = "page",
  ): Promise<void> {
    const previous = new Set(
      this.knownRequest(slug, submissionId)?.default_request_owner_ids ?? [],
    );
    const setCachedOwners = () => {
      const client = peekQueryClient();
      const row = client && findCachedRequest(client, slug, submissionId);
      if (row) this.putCachedRow(slug, { ...row, default_request_owner_ids: ownerIds });
    };
    await this.writeRow(
      slug,
      submissionId,
      async () => {
        const updated = await this.rootStore.intakeService.updateSubmissionOwners(
          slug,
          submissionId,
          ownerIds,
        );
        this.putCachedRow(slug, updated);
      },
      { optimistic: setCachedOwners },
    );
    // The picker sends the whole owner list; the events count the difference.
    const { added, removed } = listChanges(previous, ownerIds);
    if (added > 0) track("request_assigned", { request_id: submissionId, count: added, source });
    if (removed > 0) {
      track("request_unassigned", { request_id: submissionId, count: removed, source });
    }
  }

  /**
   * Move a request to another Home Team (NG-2971, NG-3153). Not optimistic: the
   * move can take the mover's own sight of the request away (spec 9.5), so the row
   * changes only once the server has it, from the server's answer.
   *
   * Then it reads the request again as the mover (NG-3182): the PATCH answers with
   * the moved row whatever the mover can see afterwards, so only a fresh read says
   * whether they still can. A 403 or 404 there records the move in
   * `movedOutOfReach`. Resolves `false` when the mover lost their access; any other
   * failure of that read leaves the page as it is.
   */
  async moveSubmissionHomeTeam(
    slug: string,
    submissionId: string,
    teamId: string,
    teamName = "",
  ): Promise<boolean> {
    await this.writeRow(slug, submissionId, async () => {
      const updated = await this.rootStore.intakeService.moveSubmissionHomeTeam(
        slug,
        submissionId,
        teamId,
      );
      this.putCachedRow(slug, updated);
    });
    try {
      const fresh = await this.rootStore.intakeService.getSubmission(slug, submissionId);
      this.writeFetchedRequest(slug, fresh);
      return true;
    } catch (err) {
      const { status } = requestErrorSummary(err);
      if (status !== 403 && status !== 404) {
        // The move saved; only the follow-up read failed, so the page stays as it
        // is rather than turning a saved move into an error.
        console.warn("[intake] moveSubmissionHomeTeam: re-reading access failed", {
          slug,
          submissionId,
          teamId,
          err,
        });
        return true;
      }
      runInAction(() => {
        this.movedOutOfReach.set(submissionId, teamName);
      });
      return false;
    }
  }

  /**
   * Pin a request's priority tier (optimistic, rolls back on error). The pinned
   * tier survives every future recompute until cleared. Optimistically shows the
   * chosen tier with ``is_overridden: true`` (keeping the last known numeric
   * ``score`` for the sort), then trues up to the server row.
   */
  async overrideSubmissionPriority(
    slug: string,
    submissionId: string,
    tier: TPriorityTier,
    source = "page",
  ): Promise<void> {
    const pinned = (s: TIntakeSubmission): TIntakeSubmission => ({
      ...s,
      priority: {
        tier,
        // Unknown until the server response trues this up (NG-1861) — the
        // picked tier's custom name is the workspace's own store label, not
        // derivable from the prior tier's payload label. `resolveTierLabel`
        // falls back to `tierLabels`/the built-in default when this is null,
        // so the optimistic row still renders a name.
        tier_label: null,
        score: s.priority?.score ?? 0,
        is_overridden: true,
        is_locked: false,
      },
    });
    const pinCachedTier = () => {
      const client = peekQueryClient();
      const row = client && findCachedRequest(client, slug, submissionId);
      if (row) this.putCachedRow(slug, pinned(row));
    };
    await this.writeRow(
      slug,
      submissionId,
      async () => {
        const updated = await this.rootStore.intakeService.overrideSubmissionPriority(
          slug,
          submissionId,
          tier,
        );
        this.putCachedRow(slug, updated);
      },
      { optimistic: pinCachedTier },
    );
    track("request_updated", {
      request_id: submissionId,
      fields: ["priority"],
      priority_override: "set",
      source,
    });
  }

  /**
   * Clear a request's priority override so it rejoins auto-compute. Unlike the
   * other inline edits this is NOT optimistic: the recomputed tier is unknowable
   * client-side (it depends on the workspace's current scoring config), so we
   * apply the server's freshly-computed row rather than guess and flicker.
   */
  async clearSubmissionPriorityOverride(
    slug: string,
    submissionId: string,
    source = "page",
  ): Promise<void> {
    await this.writeRow(slug, submissionId, async () => {
      const row = await this.rootStore.intakeService.clearSubmissionPriorityOverride(
        slug,
        submissionId,
      );
      this.putCachedRow(slug, row);
    });
    track("request_updated", {
      request_id: submissionId,
      fields: ["priority"],
      priority_override: "cleared",
      source,
    });
  }
}
